import { format, formatISO } from 'date-fns';
import {
  BLOCKED_VISITOR_ACTION,
  BLOCKED_VISITOR_STATUS,
  BLOCKED_VISITOR_TYPE,
  BlockedVisitor,
  ChatDialogue,
  ConversationSearchResult,
  CustomMetadataField,
  ImageRegistration,
  IntegrationService,
  LBBCompany,
  LBBContact,
  LBBStats,
  MunchedImage,
  OrgIntegrationService,
  SbRrwebEventsEnvelope,
  SchedulerAgentMeetPage,
  SchedulerCalendarEvent,
  SchedulerCalendarEventAdd,
  SchedulerCalendarEventType,
  SchedulerPublicAgentMeetPage,
  TimePreference,
  UniqueVisitor,
  UserIntegrationService,
  eventWithTime,
  VisitorTopPage,
  VisitorActivityResponse,
  VisitorSession,
  CallLog,
  CustomMetadataFieldDependentEntity,
  LBBDeal,
  LBBCompanyOwner,
  SchedulerCalendarPublicEvent,
  ChatMessage,
  LBBCreateContact,
  Visitor,
  VisitorActivity,
} from 'sb-lib/types';
import { clientToFe, formatUserForPost } from 'shared/utils/api';
import type { RrwebSource, StreamSource } from 'shared/utils/visitor-session-recorder';
import {
  AIChatBot,
  Account,
  AiVoice,
  AiVoiceScript,
  AiVoiceScriptCreate,
  AiVoiceScriptUpdate,
  ApiKey,
  AppNotification,
  AuditLog,
  CSVSchema,
  CSVUpload,
  CSVUploadKind,
  CalendlySettings,
  CallDisposition,
  CallTask,
  CannedResponse,
  ChatMessagesPage,
  Client,
  ClientEdit,
  ContactList,
  ContactListCallTasksResponse,
  ContactListSchema,
  Conversation,
  ConversationFilter,
  ConversationSort,
  ConversationStats,
  ConversationStatus,
  CreateAccountInfo,
  CreateCSVUpload,
  CreateSpotlightAccountListRequest,
  CreateSpotlightContactListRequest,
  CreateSpotlightIntentSignalRequest,
  Credentials,
  CrmContact,
  CrmContactCard,
  CrmContactSearchResult,
  DialSession,
  DialTask,
  Domain,
  HelpCenterArticle,
  HelpCenterArticleStatus,
  HelpCenterCollection,
  HelpCenterSettings,
  HubspotCompany,
  HubspotDealPipeline,
  HubspotSettings,
  Journey,
  JourneyAnalytics,
  JourneyGraphNodeType,
  JourneyUpdate,
  MergeTagVariable,
  NewVisitorFeed,
  OnboardingStatus,
  OnboardingStep,
  Org,
  OrgInvite,
  OrgOnboarding,
  OrgOnboardingStep,
  OrgRole,
  OrgSSO,
  OrgSettings,
  OrgWithPlan,
  OutboundCallReport,
  OutboundTranscription,
  OutboundVoicemailRecording,
  Page,
  PageParams,
  PhoneNumber,
  PolymorphicAIChatBotDataSource,
  ReplayVisibility,
  S3TranscodeUploadTicket,
  S3UploadTicket,
  SalesforceCampaign,
  SalesforceDealPipeline,
  SalesforceLeadStatus,
  SalesforceSettings,
  Segment,
  SelfAccountOrPublicAccount,
  SelfAccountWithToken,
  SelfPublicAccountWithToken,
  SelfUser,
  SessionDetails,
  SessionSummary,
  SessionSummaryActionFilter,
  SixsenseCompanyDetail,
  SlackChannel,
  SourceType,
  SpotlightAccount,
  SpotlightAccountList,
  SpotlightAccountListAnalytics,
  SpotlightAccountListUserSettings,
  SpotlightAccountsIdsResponse,
  SpotlightAccountsResponse,
  SpotlightContact,
  SpotlightContactList,
  SpotlightContactListUserSettings,
  SpotlightContactsIdsResponse,
  SpotlightContactsResponse,
  SpotlightCreateAccountRequest,
  SpotlightCreateContactRequest,
  SpotlightFilter,
  SpotlightFilterMeta,
  SpotlightIntentSignal,
  SpotlightIntentSignalKind,
  SpotlightPreferences,
  SpotlightUTM,
  SpotlightUTMs,
  TranscodeProcess,
  TranscodeSpec,
  TrusthubRegistrationBody,
  TwilioPhoneNumber,
  TwilioTokenResponse,
  UnisearchResults,
  UpdateSpotlightAccountListRequest,
  UpdateSpotlightContactListRequest,
  User,
  UserAvailability,
  UserAvailabilityArgs,
  UserGroup,
  UserIntegration,
  UserOnboarding,
  VisitorContextualRecording,
  VisitorFeed,
  VisitorJourney,
  VisitorSessionRecording,
  WidgetButtonType,
  QADataSource,
  ChatMessageUpdatePayload,
  CallSentiment,
  PatchQADataSource,
  AIChatBotDataSourceType,
  AiChatbotJobType,
  ConversationStarter,
  AIChatBotRefreshConfig,
  AIChatBotRefreshConfigCreate,
  FaqAnswer,
  AnalyticsComputationState,
} from 'types';
import {
  OrgAiAnalyticsData,
  OrgAnalyticsAgent,
  OrgAnalyticsData,
  OrgAnalyticsOrg,
  OrgOutboundAnalyticsData,
} from 'types/analytics';
import { ClearbitAccount, ClearbitVisitorRevealData } from 'types/clearbit';
import { AllProperties, Property } from 'types/property';
import { VideoBackground } from 'types/video';
import { v4 as uuidv4 } from 'uuid';
import axios from './axios';

import { PermissionGroup } from 'types/permissions';
import {
  ButtonDesignerRule,
  DesignerRule,
  DesignerRuleGroup,
  WidgetBackground,
} from 'types/designer-rules';
import {
  TimePreferenceGroup,
  TimePreferenceGroupEditable,
  TimePreferenceGroupScope,
} from 'types/time-preference-groups';
import {
  BusinessRulesConditionsSchema,
  BusinessRulesRuleName,
  BusinessRulesSelectMultipleOperator,
} from 'sb-lib/types/business-rules-types';
import { AvailabilityLog } from 'types/availability-logs';
import { SidepanelProperty, SidepanelPropertyGroup } from 'types/sidepanel';
import {
  CreateSalesfloorRoomRequest,
  SalesfloorRoom,
  SalesfloorRoomJoinResponse,
  SalesfloorRoomParticipantStatus,
  SalesfloorRoomPrivacyMode,
} from 'types/salesfloor';
import { SalesloftSettings } from 'types/salesloft';
import { AIPersona, AIPersonaAdd, AIPersonaVideoType } from 'types/ai-persona';
import { RejectError } from 'shared/api/axios';
import axiosLib, { AxiosError, AxiosResponse } from 'axios';
import {
  Goal,
  GoalListResponse,
  GoalResponse,
  GoalVersion,
  GoalVersionListResponse,
  GoalVersionResponse,
} from 'types/goal';
import { DayType } from 'types/date';
import { getLocalTimezone } from 'shared/utils/time';
import { TranscriptViewModel } from 'browser/components/Activity/TranscriptModal';

const REQUEST_TIMEOUT_HEADER = 'X-Request-Timeout';

function handleAxiosError(error: unknown): never {
  if (axiosLib.isAxiosError(error)) {
    const axiosError = error as AxiosError<RejectError>;
    if (
      axiosError.response &&
      axiosError.response.status >= 400 &&
      axiosError.response.status < 500
    ) {
      throw { ...axiosError.response.data, statusCode: axiosError.response.status };
    }
  }
  throw error;
}

async function axiosWrapper<T>(
  axiosPromise: Promise<AxiosResponse<T>>,
): Promise<AxiosResponse<T>> {
  try {
    await axiosPromise;
    return axiosPromise;
  } catch (error) {
    handleAxiosError(error);
  }
}

export function getTeam(): Promise<{ data: User[] }> {
  return axios.get(`/api/v1/org/team`);
}

export function getTeamInvites(): Promise<{
  data: OrgInvite[];
}> {
  return axios.get(`/api/v1/org/team/invites`);
}

export function sendTeamInvite(args: {
  emailAddress: string;
  roles: OrgRole[];
  onCall: boolean;
}): Promise<void> {
  return axios.post(`/api/v1/org/team/invite`, args);
}

export function removeTeamInvite(emailAddress: string): Promise<void> {
  return axios.delete(`/api/v1/org/team/invite`, { data: { emailAddress } });
}

export function updateTeamMember(
  userId: number,
  args: { roles?: OrgRole[]; onCall?: boolean },
): Promise<{ data: User }> {
  return axios.put(`/api/v1/org/team/${userId}`, args);
}

export function getInviteCode(
  code: string,
): Promise<{
  data: {
    invite: OrgInvite;
    org: OrgWithPlan;
    user: User | null;
    account: Account | null;
  };
}> {
  return axios.get(`/api/v1/org/team/invite/${code}`);
}

export function getSSORedirectUrl(email_address: string): Promise<{ data: OrgSSO[] }> {
  return axios.get(`/api/v1/sso/redirect_url`, { params: { email_address } });
}

export function createAccount(
  user: CreateAccountInfo,
): Promise<{ data: SelfAccountWithToken & { existing?: boolean } }> {
  return axios.post('/api/v1/account', {
    ...user,
    timezone: new window.Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
}

export function authAccount(
  user: Credentials,
): Promise<{
  data: SelfAccountWithToken;
}> {
  const { emailAddress, password } = user;
  return axios.post('/api/v1/account/auth', {
    email: emailAddress,
    password,
  });
}

export function ssoAuthAccount(code: string): Promise<{ data: SelfAccountWithToken }> {
  return axios.post('/api/v1/sso/auth', { code });
}

export function authSalesfloorWithLinkedIn(
  code: string,
  roomId: string,
): Promise<{ data: SelfPublicAccountWithToken }> {
  return axios.post('/api/v1/users/public-token/linkedin', { code, roomId });
}

export function logoutAccount(): Promise<void> {
  return axios.post('/api/v1/account/logout');
}

export function checkAccountAuth(): Promise<{ data: SelfAccountOrPublicAccount }> {
  return axios.get(`/api/v1/account`);
}

export function updateAccountPassword(
  currentPassword: string,
  password: string,
): Promise<{ data: { authenticationToken: string } }> {
  return axios.put(`/api/v1/account/password`, { currentPassword, password });
}

export function updateAccountEmail(
  email: string,
  password: string,
): Promise<{ data: { authenticationToken: string } }> {
  return axios.put('/api/v1/account/email', { email, password });
}

export function updateUser(user: Partial<SelfUser>): Promise<{ data: SelfUser }> {
  return axios.put(`/api/v1/users/me`, formatUserForPost(user as SelfUser));
}

export function putUserTimePreferences(
  timePreferences: TimePreference[],
): Promise<{ data: TimePreference[] }> {
  return axios.put(`/api/v1/users/time-preferences`, { timePreferences });
}

export function createTimePreferenceGroup(
  group: TimePreferenceGroupEditable,
): Promise<TimePreferenceGroup> {
  return axios.post(`/api/v1/time_preference_groups`, group);
}

export function getTimePreferenceGroups(filters?: {
  scope?: TimePreferenceGroupScope;
  userId?: number;
}): Promise<{ data: TimePreferenceGroup[] }> {
  return axios.get(`/api/v1/time_preference_groups`, {
    params: { filters: JSON.stringify(filters) },
  });
}

export function updateTimePreferenceGroup(
  group: TimePreferenceGroup,
): Promise<TimePreferenceGroup> {
  const payload: Record<string, any> = group;
  const id = group.id;
  delete payload.id;
  delete payload.userId;
  return axios.put(`/api/v1/time_preference_groups/${id}`, payload);
}

export function selectTimePreferenceGroup(
  group: TimePreferenceGroup,
): Promise<TimePreferenceGroup> {
  return axios.patch(`/api/v1/time_preference_groups/${group.id}`, { isSelected: true });
}

export function deleteTimePreferenceGroup(
  group: TimePreferenceGroup,
): Promise<TimePreferenceGroup> {
  return axios.delete(`/api/v1/time_preference_groups/${group.id}`);
}

export function getUserPermissionsGroup(
  userId: number,
): Promise<{ data: PermissionGroup[] }> {
  return axios.get(`/api/v1/users/${userId}/permission_groups`);
}

export function updateUserPermissionsGroup(
  userId: number,
  permissionGroup: PermissionGroup[],
): Promise<{ data: PermissionGroup[] }> {
  return axios.put(`/api/v1/users/${userId}/permission_groups`, {
    ...permissionGroup,
  });
}

export function joinOrgFromInvite(args: {
  code: string;
  displayName: string;
  switchOrg?: boolean;
}): Promise<{ data: SelfUser }> {
  return axios.post('/api/v1/org/team/join', args);
}

export function requestAccountRecoveryEmail(email: string): Promise<any> {
  return axios.post(`/api/v1/account/recover`, { email });
}

export function resetAccountPassword(code: string, password: string): Promise<any> {
  return axios.post(`/api/v1/account/recover/${code}`, { password });
}

export function verifyEmail(code: string): Promise<any> {
  return axios.post(`/api/v1/email/${code}/verify`);
}

export function unsubscribeEmail(code: string): Promise<any> {
  return axios.post(`/api/v1/email/${code}/unsubscribe`);
}

export function resendEmailVerification(): Promise<{ data: void }> {
  return axios.put(`/api/v1/account/resend-verification`);
}

export function getAvatarUploadTicket(
  mimetype: string,
): Promise<{ data: S3UploadTicket }> {
  return axios.post('/api/v1/users/avatar', { mimetype });
}

export function postOAuthOrgIntegration(
  code: string,
  service: OrgIntegrationService,
  extraData?: Record<string, string>,
): Promise<{ data: Org }> {
  return axios.post(`/api/v1/integrations/org/${service}`, { code, extraData });
}

export function saveOrgClearbitIntegration(args: {
  domain: string;
  email?: string;
  apiSecret?: string;
}): Promise<{ data: Org }> {
  return axios.post(`/api/v1/integrations/org/${OrgIntegrationService.clearbit}`, args);
}

export function saveOrgChiliPiperIntegration(args: {
  iframeUrl: string;
}): Promise<{ data: Org }> {
  (args as any).code = 'unused';
  return axios.post(`/api/v1/integrations/org/${OrgIntegrationService.chilipiper}`, args);
}

export function saveOrgApolloIntegration(args: {
  apiKey: string;
}): Promise<{ data: Org }> {
  (args as any).code = 'unused';
  return axios.post(`/api/v1/integrations/org/${OrgIntegrationService.apollo}`, args);
}

export function saveOrgHubSpotMeetingsIntegration(args: {
  iframeUrl: string;
}): Promise<{ data: Org }> {
  (args as any).code = 'unused';
  return axios.post(
    `/api/v1/integrations/org/${OrgIntegrationService.hubspotMeetings}`,
    args,
  );
}

export function deleteOrgIntegration(
  service: OrgIntegrationService,
): Promise<{ data: Org }> {
  return axios.delete(`/api/v1/integrations/org/${service}`);
}

export function postOrgIntegration(
  service: OrgIntegrationService,
  data: object,
): Promise<{ data: Org }> {
  return axios.post(`/api/v1/integrations/org/${service}`, data);
}

export function putOrgIntegration(
  service: OrgIntegrationService,
  data: object,
): Promise<{ data: Org }> {
  return axios.put(`/api/v1/integrations/org/${service}`, { data });
}

export function postUserIntegration(
  code: string,
  service: UserIntegrationService,
): Promise<{ data: UserIntegration[] }> {
  return axios.post(`/api/v1/integrations/user/${service}`, { code });
}

export function deleteUserIntegration(id: number): Promise<{ data: UserIntegration[] }> {
  return axios.delete(`/api/v1/integrations/user/${id}`);
}

export function putUserIntegration(
  id: number,
  data: object,
): Promise<{ data: UserIntegration[] }> {
  return axios.put(`/api/v1/integrations/user/${id}`, { data });
}

export function queryUnisearch(query: string): Promise<{ data: UnisearchResults }> {
  return axios.post(`/api/v1/org/unisearch`, { query });
}

export async function getUniqueVisitorBy(
  visitor: Partial<Client> & { path?: string },
): Promise<{ data: Client[] }> {
  // TODO: This returns UniqueVisitor, not Client. Re-type at some point.
  const r = await axios.post(`/api/v1/org/visitor_by`, visitor);
  r.data = r.data.map(clientToFe);
  return r;
}

export function searchVisitors(query: string): Promise<{ data: UniqueVisitor[] }> {
  return axios.get(`/api/v1/org/visitor?search=${query}`);
}

export function getCurrentVisitors(): Promise<{ data: { data: Visitor[] } }> {
  return axios.get(`/api/v1/org/current_visitors`);
}

export async function getUniqueVisitor(
  visitorId: string,
): Promise<{ data: UniqueVisitor }> {
  const r = await axios.get(`/api/v1/org/visitor/${visitorId}`);
  r.data = clientToFe(r.data);
  return r;
}

export async function updateUniqueVisitor(client: ClientEdit): Promise<{ data: Client }> {
  const r = await axios.patch(`/api/v1/org/visitor/${client.sbid}`, client);
  r.data = clientToFe(r.data);
  return r;
}

export function getVisitorTopPages(
  visitorId: string,
): Promise<{ data: VisitorTopPage[] }> {
  return axios.get(`/api/v1/org/visitor/${visitorId}/top_pages`);
}

export function getVisitorActions(
  visitorId: string,
  page?: number,
): Promise<{ data: VisitorActivityResponse }> {
  return axios.get(`/api/v1/org/visitor/${visitorId}/activities`, {
    params: { page },
  });
}

interface GetActivitiesParams extends PageParams {
  timestamp__gte: string;
  timestamp__lt: string;
  timestamp__dayType?: DayType;
  userId__in?: number[];
  type__in?: string[];
}

export function getActivities(
  filters: GetActivitiesParams,
): Promise<{ data: VisitorActivity[] }> {
  return axios.get(`/api/v1/org/visitor/activities`, {
    params: { filters: JSON.stringify(filters) },
  });
}

export function getActivitiesCSV(
  filters: GetActivitiesParams,
): Promise<{ data: string }> {
  return axios.get(`/api/v1/org/visitor/activities`, {
    params: { filters: JSON.stringify(filters) },
    headers: { Accept: 'text/csv' },
  });
}

export interface GetDialoguesParams extends PageParams {
  dateStarted__gte: string;
  dateStarted__lte: string;
  dateStarted__dayType?: DayType;
  hasAnswer?: boolean;
  startedBySourceType?: 'visitor';
  participantUserIds__overlap?: number[];
}

export function getDialogues(
  filters: GetDialoguesParams,
): Promise<{ data: ChatDialogue[] }> {
  return axios.get(`/api/v1/chat/dialogues`, {
    params: { filters: JSON.stringify(filters) },
  });
}

export function exportDialogues(filters: GetDialoguesParams): Promise<{ data: string }> {
  return axios.get(`/api/v1/chat/dialogues`, {
    params: { filters: JSON.stringify(filters) },
    headers: { Accept: 'text/csv' },
  });
}

export function searchCrmContact(
  query: string,
  service?: IntegrationService,
): Promise<{ data: CrmContactSearchResult[] }> {
  let querystring = `?search=${query}`;
  if (service) {
    querystring += `&service=${service}`;
  }
  return axios.get(`/api/v1/integrations/unisearch/people${querystring}`);
}

export function getCrmContact(
  visitorId: string,
): Promise<{ data: { cards: CrmContactCard[] } }> {
  return axios.get(`/api/v1/org/visitor/${visitorId}/integration_data`);
}

interface HubspotContactCreateParams {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  visitorId?: string;
}

export function postHubspotContactCreate(
  values: HubspotContactCreateParams,
): Promise<{ data: any }> {
  const newParams = { ...values, createCompany: false };
  return axios.post('/api/v1/integrations/org/hubspot/contacts', newParams);
}

interface HubspotCompanyCreateParams {
  name?: string;
  domain: string;
  city?: string;
  state?: string;
  numberofemployees?: number;
  visitorId?: string;
}

export function postHubspotCompanyCreate(
  values: HubspotCompanyCreateParams,
): Promise<{ data: any }> {
  return axios.post('/api/v1/integrations/org/hubspot/company', values);
}

export function getHubspotDealsPipelineData(): Promise<{ data: HubspotDealPipeline[] }> {
  return axios.get(`/api/v1/integrations/org/hubspot/pipelines?object=deals`);
}

interface HubspotDealCreateParams {
  amount?: string;
  closedate?: string;
  dealname: string;
  pipeline: string;
  dealstage: string;
  visitorId: string;
}

export function postHubspotDealCreate(
  values: HubspotDealCreateParams,
): Promise<{ data: any }> {
  return axios.post(`/api/v1/integrations/org/hubspot/deal`, values);
}

export function getHubspotCompany(domain: string): Promise<{ data: HubspotCompany[] }> {
  return axios.get(`/api/v1/integrations/org/hubspot/companies?domain=${domain}`);
}

export function postSalesforceLeadCreate(
  visitorId: string,
  values: any,
): Promise<{ data: any }> {
  const newParams = { ...values, visitorId: visitorId, createCompany: false };
  return axios.post('/api/v1/integrations/org/salesforce/leads', newParams);
}

interface SalesforceLeadConvertParams {
  visitorId?: string;
}

export function postSalesforceLeadConvert(
  values: SalesforceLeadConvertParams,
): Promise<{ data: any }> {
  const newParams = { ...values, createCompany: false };
  return axios.post('/api/v1/integrations/org/salesforce/leads/convert', newParams);
}

export function postSalesforceContactCreate(
  visitorId: string,
  values: any,
): Promise<{ data: any }> {
  const newParams = { ...values, visitorId, createCompany: false };
  return axios.post('/api/v1/integrations/org/salesforce/contacts', newParams);
}

export function postSalesforceCompanyCreate(
  visitorId: string,
  values: any,
): Promise<{ data: any }> {
  return axios.post('/api/v1/integrations/org/salesforce/companies', {
    ...values,
    visitorId,
  });
}

export function getSalesforceCampaigns(): Promise<{
  data: SalesforceCampaign[];
}> {
  return axios.get(`/api/v1/integrations/org/salesforce/campaigns`);
}

export function getSalesforceLeadStatuses(): Promise<{
  data: SalesforceLeadStatus[];
}> {
  return axios.get(`/api/v1/integrations/org/salesforce/lead_statuses`);
}

export function getSalesforceDealsPipelineData(): Promise<{
  data: SalesforceDealPipeline[];
}> {
  return axios.get(`/api/v1/integrations/org/salesforce/pipelines`);
}

export function postSalesforceDealCreate(
  visitorId: string,
  values: any,
): Promise<{ data: any }> {
  return axios.post(`/api/v1/integrations/org/salesforce/deals`, {
    ...values,
    visitorId,
  });
}

export function postOutreachProspectCreate(
  visitorId: string,
  values: any,
): Promise<{ data: any }> {
  return axios.post('/api/v1/integrations/org/outreach/prospects', {
    ...values,
    visitorId,
  });
}

export function getZendeskVisitorId(
  email: string | null,
  zendeskExternalId: string | null,
): Promise<{ data: { visitorId: string } }> {
  return axios.get(`/api/v1/integrations/org/zendesk/query_visitor`, {
    params: { email, zendeskExternalId },
  });
}

export function createOrgCrmContact(
  service: IntegrationService,
  data: CrmContact,
): Promise<{ data: void }> {
  if (service === OrgIntegrationService.zendesk) {
    const { email, firstName, lastName } = data;
    return axios.post(`/api/v1/integrations/org/${service}/endusers`, {
      email,
      name: `${firstName || ''} ${lastName || ''}`.trim(),
    });
  }
  return axios.post(`/api/v1/integrations/org/${service}/contacts`, data);
}

export function createVisitorBlock(
  uniqueVisitorId: string,
  note?: string,
  status?: BLOCKED_VISITOR_STATUS,
  blockType?: BLOCKED_VISITOR_TYPE,
  action?: BLOCKED_VISITOR_ACTION,
): Promise<{ data: BlockedVisitor }> {
  return axios.post(`/api/v1/visitorblock`, {
    uniqueVisitorId,
    note,
    status,
    blockType,
    action,
  });
}

export function deleteVisitorBlock(id: string): Promise<{ data: BlockedVisitor }> {
  return axios.delete(`/api/v1/visitorblock/${id}`);
}

export function getVisitorSessions(
  visitorId: string,
  params: PageParams,
): Promise<{ data: Page<VisitorSession> & { firstSession?: VisitorSession | null } }> {
  return axios.get(`/api/v1/org/visitor/${visitorId}/sessions`, { params });
}

interface GetSessionSummaryParams extends PageParams {
  actions?: SessionSummaryActionFilter[];
  companyId?: string | null;
  contactId?: string | null;
  endDate: string | number;
  startDate: string | number;
  userId?: number[] | null;
}

export async function getSessionSummaries(
  params: GetSessionSummaryParams,
): Promise<{ data: Page<SessionSummary> }> {
  return axios.get(`/api/v1/org/sessions`, { params });
}

export async function getSessionSummariesCSV(
  params: GetSessionSummaryParams,
): Promise<{ data: string }> {
  return axios.get(`/api/v1/org/sessions/csv`, { params });
}

export function getSessionDetails(
  id: string,
): Promise<{
  data: SessionDetails;
}> {
  return axios.get(`/api/v1/org/session/${id}`);
}

export function putReplayPermission(args: {
  sessionGroup: string;
  visibility: ReplayVisibility;
}): Promise<{
  data: SessionDetails;
}> {
  return axios.put(`/api/v1/replay/session/${args.sessionGroup}/permission`, {
    visibility: args.visibility,
  });
}

export async function getSessionReplay(
  sessionGroup: string,
): Promise<{ data: SessionSummary }> {
  return axios.get(`/api/v1/replay/session/${sessionGroup}`);
}

export function createStripeCheckoutSession(
  planKind: string,
): Promise<{ data: { sessionId: string } }> {
  return axios.post(`/api/v1/payment/create_checkout_session`, {
    planKind: planKind,
  });
}

export function getStripePortalUrl(): Promise<{ data: { url: string } }> {
  return axios.post(`/api/v1/payment/get_portal_url`);
}

export function updatePaymentPlan(
  planKind: string,
): Promise<{ data: { success: boolean } }> {
  return axios.post(`/api/v1/payment/update_plan`, {
    planKind: planKind,
  });
}

export function getUserOnboardingState(): Promise<{ data: UserOnboarding }> {
  return axios.get('/api/v1/users/me/onboarding');
}

export function markOnboardingStepComplete(
  step: OnboardingStep,
): Promise<{ data: UserOnboarding }> {
  return axios.put('/api/v1/users/me/onboarding/complete', { step });
}

export function getOrgOnboardingState(): Promise<{ data: OrgOnboarding }> {
  return axios.get('/api/v1/org/onboarding');
}

export function markOrgOnboardingStepComplete(
  step: OrgOnboardingStep,
): Promise<{ data: OrgOnboarding }> {
  return axios.put('/api/v1/org/onboarding/complete', { step });
}

export function createOrgDomain(url: string): Promise<{ data: Domain }> {
  return axios.post('/api/v1/domain', { url });
}

export function removeOrgDomain(id: number): Promise<void> {
  return axios.delete(`/api/v1/domain/${id}`);
}

export function createCannedResponse(
  code: string,
  content: string,
): Promise<{ data: CannedResponse }> {
  return axios.post(`/api/v1/canned_responses`, { code, content });
}

export function updateCannedResponse(
  cannedResponse: Pick<CannedResponse, 'id' | 'code' | 'content'>,
): Promise<{ data: CannedResponse }> {
  const { id, ...params } = cannedResponse;
  return axios.put(`/api/v1/canned_responses/${id}`, params);
}

export function getCannedResponses(): Promise<{ data: CannedResponse[] }> {
  return axios.get(`/api/v1/canned_responses`);
}

export function getCannedResponse(id: number): Promise<{ data: CannedResponse }> {
  return axios.get(`/api/v1/canned_responses/${id}`);
}

export function deleteCannedResponse(id: number): Promise<void> {
  return axios.delete(`/api/v1/canned_responses/${id}`);
}

export function createUserGroup(name: string): Promise<{ data: UserGroup }> {
  return axios.post(`/api/v1/user_groups`, { name });
}

/**
 * User Groups
 */

export function addUserGroupUser(
  userGroupId: number,
  userId: number,
): Promise<{ data: UserGroup }> {
  return axios.post(`/api/v1/user_groups/${userGroupId}/users`, { userId });
}

export function deleteUserGroupUser(
  userGroupId: number,
  userId: number,
): Promise<{ data: UserGroup }> {
  return axios.delete(`/api/v1/user_groups/${userGroupId}/users/${userId}`);
}

export function updateUserGroup(
  userGroup: Pick<UserGroup, 'id' | 'name'>,
): Promise<{ data: UserGroup }> {
  const { id, ...params } = userGroup;
  return axios.patch(`/api/v1/user_groups/${id}`, params);
}

export function getUserGroups(): Promise<{ data: UserGroup[] }> {
  return axios.get(`/api/v1/user_groups`);
}

export function getUserGroup(id: number): Promise<{ data: UserGroup }> {
  return axios.get(`/api/v1/user_groups/${id}`);
}

export function deleteUserGroup(id: number): Promise<void> {
  return axios.delete(`/api/v1/user_groups/${id}`);
}

/**
 * Segments
 */

export function createSegment(name: string): Promise<{ data: Segment }> {
  return axios.post(`/api/v1/segments`, {
    name,
    conditions: {
      all: [{ name: 'url', operator: 'starts_with', value: 'https://' }],
    },
  });
}

export function updateSegment(segment: Partial<Segment>): Promise<{ data: Segment }> {
  const { id, ...params } = segment;
  return axios.patch(`/api/v1/segments/${id}`, params);
}

export function getSegments(): Promise<{ data: Segment[] }> {
  return axios.get(`/api/v1/segments`);
}

export function getDesignerButtonRules(
  scope: WidgetButtonType,
): Promise<{ data: ButtonDesignerRule[] }> {
  return axios.get(`/api/v1/designer/rules/${scope}_button`);
}

function makeButtonRulePayload(segmentIds: number[]) {
  const conditions = {
    any: [
      {
        name: BusinessRulesRuleName.segments,
        operator: BusinessRulesSelectMultipleOperator.shares_at_least_one_element_with,
        value: segmentIds,
      },
    ],
  };

  return {
    name: 'Button rule',
    conditions: conditions,
    order: 0,
    enabled: true,
    actions: [
      {
        order: 0,
        operator: 'toggle_button_visibility',
      },
    ],
  };
}

export function createDesignerButtonRules(scope: WidgetButtonType, segmentIds: number[]) {
  const payload = makeButtonRulePayload(segmentIds);
  return axios.post(`/api/v1/designer/rules/${scope}_button`, payload);
}

export function updateDesignerButtonRules(
  scope: WidgetButtonType,
  id: number,
  segmentIds: number[],
) {
  const payload = makeButtonRulePayload(segmentIds);
  return axios.put(`/api/v1/designer/rules/${scope}_button/${id}`, payload);
}

export function deleteDesignerButtonRules(scope: WidgetButtonType, id: number) {
  return axios.delete(`/api/v1/designer/rules/${scope}_button/${id}`);
}

export function getWidgetBackgrounds(): Promise<{ data: WidgetBackground[] }> {
  return axios.get(`/api/v1/designer/widget_backgrounds`);
}

export function createWidgetBackground(
  background: Omit<WidgetBackground, 'id'>,
): Promise<{ data: WidgetBackground }> {
  return axios.post(`/api/v1/designer/widget_backgrounds`, background);
}

export function deleteWidgetBackground(id: number): Promise<void> {
  return axios.delete(`/api/v1/designer/widget_backgrounds/${id}`);
}

export function getSegmentConditionsSchema(): Promise<{
  data: BusinessRulesConditionsSchema;
}> {
  return axios.get(`/api/v1/segments/conditions_schema`);
}

export function deleteSegment(id: number): Promise<void> {
  return axios.delete(`/api/v1/segments/${id}`);
}

export function getDesignerRules(
  group: DesignerRuleGroup = DesignerRuleGroup.GENERAL,
): Promise<{
  data: DesignerRule[];
}> {
  return axios.get(`/api/v1/designer/rules/${group}`);
}

export function createDesignerRule(
  group: DesignerRuleGroup = DesignerRuleGroup.GENERAL,
  payload: DesignerRule,
) {
  return axios.post(`/api/v1/designer/rules/${group}`, payload);
}

export function deleteDesignerRule(
  group: DesignerRuleGroup = DesignerRuleGroup.GENERAL,
  id: number,
) {
  return axios.delete(`/api/v1/designer/rules/${group}/${id}`);
}

export function getDesignerRule(
  group: DesignerRuleGroup = DesignerRuleGroup.GENERAL,
  id: number,
) {
  return axios.get(`/api/v1/designer/rules/${group}/${id}`);
}

export function updateDesignerRule(
  group: DesignerRuleGroup = DesignerRuleGroup.GENERAL,
  id: number,
  payload: DesignerRule,
) {
  return axios.put(`/api/v1/designer/rules/${group}/${id}`, payload);
}

export function getDesignerRulesSchema(
  group: DesignerRuleGroup = DesignerRuleGroup.GENERAL,
) {
  return axios.get(`/api/v1/designer/rules/${group}/schema`);
}

/**
 * Alerts
 */

export function getAlertConditionsSchema(): Promise<{
  data: BusinessRulesConditionsSchema;
}> {
  return axios.get(`/api/v1/alerts/visitor/conditions_schema`);
}

/**
 * Routing Rules
 */

export function getRoutingRuleConditionsSchema(): Promise<{
  data: BusinessRulesConditionsSchema;
}> {
  return axios.get(`/api/v1/routing_rules/conditions_schema`);
}

/**
 * Journeys
 */

export function createDefaultJourney(name: string): Promise<{ data: Journey }> {
  const rootNodeId = uuidv4();
  return axios.post(`/api/v1/journeys`, {
    name,
    conditions: {
      all: [{ name: 'url', operator: 'starts_with', value: 'https://' }],
    },
    graph: {
      root: rootNodeId,
      graph: { [rootNodeId]: {} },
      nodes: { [rootNodeId]: { type: JourneyGraphNodeType.unknown, data: {} } },
    },
  });
}

export function createJourney(
  data: Omit<JourneyUpdate, 'id'>,
): Promise<{ data: Journey }> {
  return axios.post(`/api/v1/journeys`, data);
}

export function updateJourney(journey: JourneyUpdate): Promise<{ data: Journey }> {
  const { id, ...params } = journey;
  return axios.patch(`/api/v1/journeys/${id}`, params);
}

export function getJourneys({
  active = undefined,
  page,
  pageSize = 50,
}: {
  active?: boolean;
  page: number;
  pageSize: number;
}): Promise<{ data: Page<Journey> }> {
  const filters = JSON.stringify({ active });
  return axios.get(`/api/v1/journeys`, { params: { filters, page, pageSize } });
}

export function getJourney(id: number): Promise<{ data: Journey }> {
  return axios.get(`/api/v1/journeys/${id}`);
}

export function getJourneyConditionsSchema(): Promise<{
  data: BusinessRulesConditionsSchema;
}> {
  return axios.get(`/api/v1/journeys/conditions_schema`);
}

export function getJourneyChatMessageVariables(): Promise<{
  data: { chatMessageVariables: MergeTagVariable[] };
}> {
  return axios.get(`/api/v1/journeys/chat_message_variables`);
}

export function getJourneyAnalytics(
  id: number,
  startDate: Date,
  endDate: Date,
  versionId?: number,
): Promise<{ data: JourneyAnalytics }> {
  return axios.get(`/api/v1/journeys/${id}/analytics`, {
    params: {
      version_id: versionId,
      start_time: formatISO(startDate),
      end_time: formatISO(endDate),
    },
  });
}

export function deleteJourney(id: number): Promise<void> {
  return axios.delete(`/api/v1/journeys/${id}`);
}

export function getVisitorJourneys(
  journeyVersionId: number,
): Promise<{ data: Array<VisitorJourney> }> {
  const filters = JSON.stringify({ journeyVersionId });
  return axios.get(`/api/v1/journeys/visitor_journeys`, { params: { filters } });
}

export function createWidgetTestToken(): Promise<{ data: { token: string } }> {
  return axios.post(`/api/v1/widget_test/token`);
}

export function updateOrgSettings(
  settings: Partial<OrgSettings>,
): Promise<{ data: Org }> {
  return axios.put('/api/v1/org/settings', settings);
}

export function getOrgSettings(): Promise<{ data: Org }> {
  return axios.get('/api/v1/org/settings');
}

export function getOrgSettingsSchema() {
  return axios.get('/api/v1/org/settings/schema');
}

export function getHelpCenterSettings(): Promise<{ data: HelpCenterSettings }> {
  return axios.get(`/api/v1/helpcenter/settings`);
}

export function updateHelpCenterSettings(
  settings: Partial<HelpCenterSettings>,
): Promise<{ data: HelpCenterSettings }> {
  return axios.patch(`/api/v1/helpcenter/settings`, settings);
}

export function getHubspotSettings(): Promise<{ data: HubspotSettings }> {
  return axios.get(`/api/v1/integrations/org/hubspot/settings`);
}

export function getHubspotActivityTypes() {
  return axios.get(`/api/v1/integrations/org/hubspot/activity-types`);
}

export function updateHubspotSettings(
  settings: Partial<HubspotSettings>,
): Promise<{ data: HubspotSettings }> {
  return axios.put('/api/v1/integrations/org/hubspot/settings', settings);
}

export function getSalesforceSettings(): Promise<{ data: SalesforceSettings }> {
  return axios.get(`/api/v1/integrations/org/salesforce/settings`);
}

export function updateSalesforceSettings(
  settings: Partial<SalesforceSettings>,
): Promise<{ data: SalesforceSettings }> {
  return axios.put('/api/v1/integrations/org/salesforce/settings', settings);
}

export function getPushPubkey(): Promise<{ data: { pubkey: string } }> {
  return axios.get('/api/v1/notifications/push/pubkey');
}

export function savePushSubscription(
  sub: PushSubscriptionJSON & { device: string },
): Promise<{ data: PushSubscription }> {
  return axios.post('/api/v1/notifications/push/subscribe', sub);
}

export function deletePushSubscription(params: {
  id?: number;
  endpoint?: string;
}): Promise<void> {
  return axios.delete('/api/v1/notifications/push', { params });
}

export function removeOrgMember(id: number): Promise<void> {
  return axios.delete(`/api/v1/org/team/${id}`);
}

export function getOrgSlackChannels(): Promise<{ data: { channels: SlackChannel[] } }> {
  return axios.get('/api/v1/integrations/org/slack/channels');
}

export function postAdminFeedback(args: {
  message: string;
}): Promise<{ data: { ok: boolean } }> {
  return axios.post('/api/v1/feedback', args);
}

export interface AdminReportArgs {
  message: string;
  visitorId?: string;
  visitorSid?: string;
  visitorUserAgent?: string;
}

export function postAdminReport(
  args: AdminReportArgs,
): Promise<{ data: { ok: boolean } }> {
  return axios.post('/api/v1/feedback/report', args);
}

export function createOrg(args: Pick<Org, 'name'>): Promise<{ data: SelfUser }> {
  return axios.post('/api/v1/org', args);
}

export function getOrgClearbitAccount(): Promise<{ data: ClearbitAccount }> {
  return axios.get(`/api/v1/clearbit/account`);
}

export function getClearbitVisitorRevealData(
  uvid: string,
): Promise<{ data: ClearbitVisitorRevealData }> {
  return axios.get(`/api/v1/clearbit/${uvid}/reveal`);
}

export function postClearbitVisitorRevealData(
  uvid: string,
): Promise<{ data: ClearbitVisitorRevealData }> {
  return axios.post(`/api/v1/clearbit/${uvid}/reveal`);
}

export function saveOrgSixsenseIntegration(args: {
  apiKey: string;
}): Promise<{ data: Org }> {
  return axios.post(`/api/v1/integrations/org/sixsense`, args);
}

export function getSixsenseCompanyDetailData(
  uvid: string,
): Promise<{ data: SixsenseCompanyDetail }> {
  return axios.get(`/api/v1/sixsense/company_detail/${uvid}`);
}

export function postSixsenseCompanyDetailData(
  uniqueVisitorId: string,
): Promise<{ data: SixsenseCompanyDetail }> {
  return axios.post(`/api/v1/sixsense/company_detail`, { uniqueVisitorId });
}

export function postInternalAuthImpersonate(
  code: string,
): Promise<{ data: SelfAccountWithToken }> {
  return axios.post(`/api/v1/internal/impersonate/auth`, { code });
}

export function getOrgAssetUploadTicket(
  asset:
    | 'widget-photo'
    | 'widget-video'
    | 'widget-org-logo'
    | 'helpcenter-org-logo'
    | 'helpcenter-org-background',
  mimetype: string,
): Promise<{ data: S3UploadTicket }> {
  return axios.post('/api/v1/org/settings/upload', { asset, mimetype });
}

export function getTranscodeUploadTicket(
  filename: string,
): Promise<{ data: S3TranscodeUploadTicket }> {
  return axios.post('/api/v1/transcodes/sources', { filename });
}

export function startTranscode(
  id: string,
  transcodeSpecName: TranscodeSpec,
): Promise<{ data: TranscodeProcess }> {
  return axios.post(`/api/v1/transcodes/sources/${id}/proxies`, { transcodeSpecName });
}

export function getTranscodeStatus(id: string): Promise<{ data: TranscodeProcess }> {
  return axios.get(`/api/v1/transcodes/proxies/${id}`);
}

export function getOrgAnalytics(
  startDate: string,
  endDate: string,
  dayType?: DayType | null,
): Promise<{ data: OrgAnalyticsData }> {
  const timezone = dayType ? getLocalTimezone() : undefined;
  return axios.get(`/api/v1/analytics/org`, {
    params: { startDate, endDate, timezone, dayType: dayType || undefined },
  });
}

export function getAnalyticsComputationState(): Promise<{
  data: AnalyticsComputationState;
}> {
  return axios.get(`/api/v1/analytics/computation_state`);
}

export function getOrgAnalyticsTimeseries(
  startDate: string,
  endDate: string,
  dimension: 'org' | 'path' | 'user',
  dayType?: DayType | null,
): Promise<{ data: OrgAnalyticsOrg[] | OrgAnalyticsAgent[] }> {
  const timezone = dayType ? getLocalTimezone() : undefined;
  return axios.get(`/api/v1/analytics/timeseries/${dimension}`, {
    params: { startDate, endDate, timezone, dayType: dayType || undefined },
  });
}

export function getOrgAnalyticsCSV(
  resource: 'overall' | 'pages' | 'members',
  startDate: string,
  endDate: string,
  dayType?: DayType | null,
): Promise<{ data: string }> {
  const timezone = dayType ? getLocalTimezone() : undefined;
  return axios.get(`/api/v1/analytics/org/csv`, {
    params: { resource, startDate, endDate, timezone, dayType: dayType || undefined },
  });
}

export function getOrgOutboundAnalytics(
  startDate: string,
  endDate: string,
): Promise<{ data: OrgOutboundAnalyticsData }> {
  return axios.get(`/api/v1/analytics/org/outbound`, { params: { startDate, endDate } });
}

export function getOrgAiAnalytics(
  startDate: string,
  endDate: string,
): Promise<{ data: OrgAiAnalyticsData }> {
  return axios.get(`/api/v1/analytics/org/ai`, { params: { startDate, endDate } });
}

export function getOrgOutboundAnalyticsCSV(
  resource: 'lists' | 'reps',
  startDate: string,
  endDate: string,
): Promise<{ data: string }> {
  return axios.get(`/api/v1/analytics/org/outbound/csv`, {
    params: { resource, startDate, endDate },
  });
}

export function postSessionRecording(
  visitorId: string,
  mimetype: string,
): Promise<{ data: VisitorSessionRecording }> {
  return axios.post('/api/v1/recording/session', { visitorId, mimetype });
}

export function postSessionRecordingPart(
  recordingId: string,
  streamer: StreamSource,
  partNumber: number,
): Promise<{ data: { url: string } }> {
  return axios.post(`/api/v1/recording/session/${recordingId}/part`, {
    streamer,
    partNumber,
  });
}

export function putSessionRecordingPartUploaded(
  recordingId: string,
  streamer: StreamSource,
  partNumber: number,
  etag: string,
): Promise<void> {
  return axios.put(`/api/v1/recording/session/${recordingId}/part/uploaded`, {
    partNumber,
    streamer,
    etag,
  });
}

export function putSessionRecordingRrwebEvents(
  recordingId: string,
  source: RrwebSource,
  events: eventWithTime[],
): Promise<void> {
  return axios.put(`/api/v1/recording/session/${recordingId}/rrweb`, {
    source,
    events,
  });
}

export function putSessionRecordingComplete(
  recordingId: string,
  duration: number,
): Promise<{ data: VisitorSessionRecording }> {
  return axios.put(`/api/v1/recording/session/${recordingId}/complete`, { duration });
}

export async function getBlockedVisitors(
  params: PageParams,
): Promise<{ data: Page<BlockedVisitor> }> {
  return axios.get(`/api/v1/visitorblock`, { params });
}

export async function getAuditLogs(
  params: PageParams,
): Promise<{ data: Page<AuditLog> }> {
  return axios.get(`/api/v1/audit_logs`, { params });
}

export async function getAvailabilityLogs(
  params: PageParams,
): Promise<{ data: Page<AvailabilityLog> }> {
  return axios.get(`/api/v1/availability_logs`, { params });
}

export async function getApiKeys(): Promise<{ data: ApiKey[] }> {
  return axios.get(`/api/v1/org/apikeys`);
}

export function postApiKey(args: {
  name: string;
  active: boolean;
}): Promise<{ data: { apiKey: ApiKey; secretKey: string } }> {
  return axios.post(`/api/v1/org/apikeys`, args);
}

export function putApiKey(args: {
  apiKeyId: string;
  name: string;
  active: boolean;
}): Promise<{ data: ApiKey }> {
  const { apiKeyId, name, active } = args;
  return axios.put(`/api/v1/org/apikeys/${apiKeyId}`, { name, active });
}

export function deleteApiKey(apiKeyId: string): Promise<void> {
  return axios.delete(`/api/v1/org/apikeys/${apiKeyId}`);
}

export function getUserAvailability(): Promise<{ data: UserAvailability }> {
  return axios.get(`/api/v1/users/me/availability`);
}

export function postUserAvailability(
  args: UserAvailabilityArgs,
): Promise<{ data: UserAvailability }> {
  return axios.post(`/api/v1/users/me/availability`, args);
}

export function patchUserAvailability(
  id: UserAvailability['id'],
  args: UserAvailabilityArgs,
): Promise<{ data: UserAvailability }> {
  return axios.patch(`/api/v1/users/me/availability/${id}`, args);
}

export function deleteUserAvailability(
  id: UserAvailability['id'],
): Promise<{ data: UserAvailability }> {
  return axios.delete(`/api/v1/users/me/availability/${id}`);
}

interface SearchConversationParams extends PageParams {
  startDate: string | number;
  endDate: string | number;
  text: string;
  visitorId?: string | null;
  userId?: number | null;
  assigneeId?: number | null;
}

export async function getConversationSearch(
  params: SearchConversationParams,
): Promise<{ data: Page<ConversationSearchResult> }> {
  if (!params.assigneeId) {
    delete params.assigneeId;
  }
  return axios.get(`/api/v1/chat/messages/search`, { params });
}

export function getConversationStats(
  groupId?: number | null,
): Promise<{ data: ConversationStats }> {
  return axios.get(`/api/v1/chat/inbox/stats`, {
    params: { groupId: groupId || undefined },
  });
}

export function getConversation(conversationId: number): Promise<{ data: Conversation }> {
  return axios.get(`/api/v1/chat/conversations/${conversationId}`);
}

export function putConversationRead(conversationId: number): Promise<{ data: void }> {
  return axios.put(`/api/v1/chat/conversations/${conversationId}/read`);
}

export function getVisitorConversation(
  visitorId: string,
): Promise<{ data: Conversation }> {
  return axios.get(`/api/v1/chat/visitor/${visitorId}/conversations/default`);
}

export function closeConversation(
  conversationId: number,
): Promise<{ data: Conversation }> {
  return axios.post(`/api/v1/chat/conversations/${conversationId}/close`);
}

export function openConversation(
  conversationId: number,
): Promise<{ data: Conversation }> {
  return axios.post(`/api/v1/chat/conversations/${conversationId}/open`);
}

export function postConversationAssigneeId({
  conversationId,
  assigneeId,
}: {
  conversationId: number;
  assigneeId: number | null;
}): Promise<{ data: Conversation }> {
  return axios.post(`/api/v1/chat/conversations/${conversationId}/assign`, {
    assigneeId,
  });
}

export function postConversationGroupId({
  conversationId,
  groupId,
}: {
  conversationId: number;
  groupId: number | null;
}): Promise<{ data: Conversation }> {
  return axios.post(`/api/v1/chat/conversations/${conversationId}/assign_group`, {
    groupId: groupId,
  });
}

export function patchChatMessage(
  chatMessageId: string,
  payload: ChatMessageUpdatePayload,
): Promise<{ data: ChatMessage }> {
  return axios.patch(`/api/v1/chat/messages/${chatMessageId}`, payload);
}

interface ConversationsFiltersParams {
  visitorId?: string | null;
  assigneeId?: number | null;
  userGroupId?: number | null;
  mentionedUserId?: number | null;
  dateStarted__gt?: string | null;
  dateStarted__lt?: string | null;
  dateEnded?: number | null;
  dateEnded__ne?: number | null;
  dateLastMessage__lt?: string | null;
  dateLastMessage__gt?: string | null;
  hasUnreads?: boolean;
  aiPersonaId__ne?: number | null;
}

export function getConversations({
  page,
  userId,
  visitorId,
  filter,
  status,
  sort,
  dateLastMessage,
  pageSize = 50,
  hasUnreads,
  userGroupId,
  dateStart,
  dateEnd,
}: {
  page: number;
  userId?: number;
  visitorId?: string;
  filter?: ConversationFilter;
  status?: ConversationStatus;
  sort: ConversationSort;
  dateLastMessage?: number;
  dateStart?: string;
  dateEnd?: string;
  pageSize?: number;
  hasUnreads?: boolean;
  userGroupId?: number;
}): Promise<{ data: Page<Conversation> }> {
  const params = { filters: [], sort, page, pageSize } as PageParams;
  const filters = {} as ConversationsFiltersParams;

  if (visitorId) {
    filters.visitorId = visitorId;
  }
  if (filter === ConversationFilter.UNASSIGNED) {
    filters.assigneeId = null;
  }
  if (filter === ConversationFilter.ASSIGNED) {
    filters.assigneeId = userId;
  }
  if (filter === ConversationFilter.ALL) {
    filters.userGroupId = userGroupId;
  }
  if (filter === ConversationFilter.MENTIONED) {
    filters.mentionedUserId = userId;
  }
  if (filter === ConversationFilter.AI) {
    filters.aiPersonaId__ne = null;
  }
  if (status === ConversationStatus.OPEN) {
    filters.dateEnded = null;
  }
  if (status === ConversationStatus.CLOSED) {
    filters.dateEnded__ne = null;
  }
  if (sort === ConversationSort.NEWEST) {
    params.sort = 'dateLastMessage__desc';
    if (dateLastMessage) {
      filters.dateLastMessage__lt = new Date(dateLastMessage).toISOString();
    }
  }
  if (sort === ConversationSort.OLDEST) {
    params.sort = 'dateLastMessage__asc';
    if (dateLastMessage) {
      filters.dateLastMessage__gt = new Date(dateLastMessage).toISOString();
    }
  }
  if (hasUnreads) {
    filters.hasUnreads = true;
  }
  if (dateStart) {
    filters.dateStarted__gt = new Date(dateStart).toISOString();
  }
  if (dateEnd) {
    const endOfDay = new Date(dateEnd);
    endOfDay.setHours(23, 59, 59, 999);
    filters.dateStarted__lt = endOfDay.toISOString();
  }
  params.filters = [JSON.stringify(filters)];
  return axios.get(`/api/v1/chat/conversations`, { params });
}

export function getChats(
  visitorId: string,
  cursor?: null | undefined | number,
): Promise<{ data: ChatMessagesPage }> {
  const params = { visitorId, cursor };
  if (!cursor) delete params.cursor;
  return axios.get(`/api/v1/chat/visitor/${visitorId}`, {
    params,
  });
}

export function getChatHistoryCSV(): Promise<{ data: string }> {
  return axios.get(`/api/v1/chat/export_chat_history`);
}

export function getConversationMessages(
  conversationId: number,
  cursor?: null | undefined | number,
): Promise<{ data: ChatMessagesPage }> {
  const params = { cursor };
  if (!cursor) delete params.cursor;
  return axios.get(`/api/v1/chat/conversations/${conversationId}/messages`, {
    params,
  });
}

export function postImage(filename: string): Promise<{ data: ImageRegistration }> {
  return axios.post(`/api/v1/images`, { filename });
}

export function getImage(
  imageSourceId: string,
  wait: boolean,
): Promise<{ data: MunchedImage }> {
  return axios.get(`/api/v1/images/${imageSourceId}?wait=${wait}`);
}

export function getSnapshot(
  ticket: string,
): Promise<{ data: SbRrwebEventsEnvelope | SbRrwebEventsEnvelope[] }> {
  return axios.get(`/api/v1/users/snapshot/${ticket}`);
}

export async function getHelpCenterArticles(
  params: PageParams,
): Promise<{ data: Page<HelpCenterArticle> }> {
  return axios.get(`/api/v1/helpcenter/articles`, { params });
}

export async function getHelpCenterArticle(
  articleId: string,
): Promise<{ data: HelpCenterArticle }> {
  return axios.get(`/api/v1/helpcenter/articles/${articleId}`);
}

export function createHelpCenterArticle(
  title: string,
  description: string | undefined,
  content: string,
  userId: number,
  status: HelpCenterArticleStatus,
  articleCollectionId: number | undefined,
  imageIds: string[],
): Promise<{ data: HelpCenterArticle }> {
  return axios.post(`/api/v1/helpcenter/articles`, {
    title,
    description,
    content,
    userId,
    status,
    articleCollectionId,
    imageIds,
  });
}

export function updateHelpCenterArticle(
  helpCenterArticle: Partial<HelpCenterArticle>,
): Promise<{ data: HelpCenterArticle }> {
  const { id, ...params } = helpCenterArticle;
  return axios.patch(`/api/v1/helpcenter/articles/${id}`, params);
}

export function deleteHelpCenterArticle(id: number): Promise<void> {
  return axios.delete(`/api/v1/helpcenter/articles/${id}`);
}

export async function getHelpCenterCollections(): Promise<{
  data: HelpCenterCollection[];
}> {
  return axios.get(`/api/v1/helpcenter/article-collections`);
}

export async function getHelpCenterCollection(
  collectionId: string,
): Promise<{ data: HelpCenterCollection }> {
  return axios.get(`/api/v1/helpcenter/article-collections/${collectionId}`);
}

export function createHelpCenterCollection(
  title: string,
  color: string,
): Promise<{ data: HelpCenterCollection }> {
  return axios.post(`/api/v1/helpcenter/article-collections`, {
    title,
    color,
  });
}

export function updateHelpCenterCollection(
  helpCenterCollection: Partial<HelpCenterCollection>,
): Promise<{ data: HelpCenterCollection }> {
  const { id, ...params } = helpCenterCollection;
  return axios.patch(`/api/v1/helpcenter/article-collections/${id}`, params);
}

export function deleteHelpCenterCollection(id: number): Promise<void> {
  return axios.delete(`/api/v1/helpcenter/article-collections/${id}`);
}

export function reassignHelpCenterCollection(id: number, id2: number): Promise<void> {
  return axios.put(`/api/v1/helpcenter/article-collections/${id}/reassign_collection`, {
    new_collection_id: id2,
  });
}

export async function getHelpCenterDomainLink(): Promise<{
  data: any;
}> {
  return axios.get(`/api/v1/helpcenter/domainlink`);
}

export async function createHelpCenterDomainLink(
  domain: String,
): Promise<{
  data: any;
}> {
  return axios.post(`/api/v1/helpcenter/domainlink`, {
    domain: domain,
  });
}

export async function deleteHelpCenterDomainLink(): Promise<{
  data: any;
}> {
  return axios.delete(`/api/v1/helpcenter/domainlink`);
}

export function getSecureIdentityKey(): Promise<{ data: { secureIdentityKey: string } }> {
  return axios.get(`/api/v1/org/secureidentitykey`);
}

export function putRequireSecureIdentities(required: boolean): Promise<{ data: Org }> {
  return axios.put(`/api/v1/org/secureidentitykey/required`, { required });
}

export function postRotateSecureIdentityKey(): Promise<{
  data: { secureIdentityKey: string };
}> {
  return axios.post(`/api/v1/org/secureidentitykey/rotate`);
}

export function getOrgCalendlySettings(): Promise<{ data: CalendlySettings }> {
  return axios.get(`/api/v1/integrations/org/${OrgIntegrationService.calendly}/settings`);
}

// FIXME(bogdan): unused
export function getOrgHubspotPropertyMappings(): Promise<{ data: any }> {
  return axios.get(`/api/v1/integrations/org/${OrgIntegrationService.hubspot}/mappings`);
}

export function getOrgHubspotObjectProperties(): Promise<{ data: any }> {
  return axios.get(
    `/api/v1/integrations/org/${OrgIntegrationService.hubspot}/object_properties`,
  );
}

// FIXME(bogdan): unused
export function postOrgHubspotPropertyMappings(mappings: any): Promise<{ data: any }> {
  return axios.post(
    `/api/v1/integrations/org/${OrgIntegrationService.hubspot}/mappings`,
    { mappings: mappings },
  );
}

export function getOrgOutreachObjectProperties(): Promise<{ data: any }> {
  return axios.get(
    `/api/v1/integrations/org/${OrgIntegrationService.outreach}/object_properties`,
  );
}

export function getOrgSalesforceObjectProperties(): Promise<{ data: any }> {
  return axios.get(
    `/api/v1/integrations/org/${OrgIntegrationService.salesforce}/object_properties`,
  );
}

export function getVisitorContextualRecordings(
  visitorId: string,
  page: number,
): Promise<{ data: Page<VisitorContextualRecording> }> {
  return axios.get(`/api/v1/contextual/recordings/${visitorId}`, {
    params: { page },
  });
}

export function createSchedulerCalendarEventType(
  data: Partial<SchedulerCalendarEventType>,
): Promise<{ data: SchedulerCalendarEventType }> {
  const params = data as any;
  if (params.earliestDate) {
    params.earliestDate = format(params.earliestDate, 'yyyy-MM-dd');
  }
  if (params.latestDate) {
    params.latestDate = format(params.latestDate, 'yyyy-MM-dd');
  }
  return axios.post(`/api/v1/scheduler/event_types`, data);
}

export function updateSchedulerCalendarEventType(
  data: Partial<SchedulerCalendarEventType>,
): Promise<{ data: SchedulerCalendarEventType }> {
  const { id, ...params } = data;
  delete params.userId;
  delete params.nextUserId;
  if (params.earliestDate && params.earliestDate instanceof Date) {
    params.earliestDate = format(params.earliestDate, 'yyyy-MM-dd');
  }
  if (params.latestDate && params.latestDate instanceof Date) {
    params.latestDate = format(params.latestDate, 'yyyy-MM-dd');
  }
  return axios.patch(`/api/v1/scheduler/event_types/${id}`, params);
}

interface GetSchedulerCalendarEventsParams extends PageParams {
  isPast: boolean;
  showCancelled: boolean;
  userIds: number[] | null;
}

interface GetSchedulerCalendarEventsFilters {
  startTime__lt?: string;
  startTime__gte?: string;
  dateCreated__lt?: string;
  dateCreated__gte?: string;
  dateCreated__dayType?: DayType;
  sourceType?: string;
  active?: boolean;
  'attendees.userId__in'?: number[];
}

export function getSchedulerCalendarEvents(
  p: GetSchedulerCalendarEventsParams,
): Promise<{
  data: Page<SchedulerCalendarEvent>;
}> {
  const { isPast, showCancelled, userIds, ...params } = p;
  const filters = {} as GetSchedulerCalendarEventsFilters;

  if (isPast) {
    filters.startTime__lt = formatISO(Date.now());
    params.sort = 'startTime__desc';
  } else {
    filters.startTime__gte = formatISO(Date.now());
    params.sort = 'startTime__asc';
  }
  if (!showCancelled) {
    filters.active = true;
  }
  if (userIds && userIds.length > 0) {
    filters['attendees.userId__in'] = [...new Set(userIds)];
  }

  params.filters = [JSON.stringify(filters)];
  return axios.get(`/api/v1/scheduler/events`, { params });
}

export function getSchedulerCalendarEventsCSV(
  filters: GetSchedulerCalendarEventsFilters,
): Promise<{ data: string }> {
  return axios.get(`/api/v1/scheduler/events`, {
    params: { filters: JSON.stringify(filters) },
    headers: { Accept: 'text/csv' },
  });
}

interface GetCalendarEventsParams extends PageParams {
  startDate?: string;
  endDate?: string;
  sourceType?: string;
  userId?: number[];
  dayType?: DayType;
}

export function getCalendarEvents(
  p: GetCalendarEventsParams,
): Promise<{
  data: SchedulerCalendarEvent[];
}> {
  const { startDate, endDate, sourceType, ...params } = p;
  const filters = {} as GetSchedulerCalendarEventsFilters;

  if (startDate && endDate) {
    filters.dateCreated__gte = startDate;
    filters.dateCreated__lt = endDate;
    params.sort = 'startTime__desc';
  }
  if (sourceType) {
    filters.sourceType = sourceType;
  }
  if (p.userId) {
    filters['attendees.userId__in'] = p.userId;
  }
  if (p.dayType) {
    filters['dateCreated__dayType'] = p.dayType;
  }

  params.filters = [JSON.stringify(filters)];
  return axios.get(`/api/v1/scheduler/events`, { params });
}

export function patchSchedulerCalendarEvent(
  id: number,
  startTime: Date,
): Promise<{
  data: SchedulerCalendarEvent;
}> {
  return axios.patch(`/api/v1/scheduler/events/${id}`, {
    startTime: formatISO(startTime),
  });
}

export function deleteSchedulerCalendarEvent(
  id: number,
  message: string,
): Promise<{
  data: SchedulerCalendarEvent;
}> {
  return axios.delete(`/api/v1/scheduler/events/${id}`, { data: { message } });
}

export function getSchedulerOrgCalendarEventTypes(): Promise<{
  data: SchedulerCalendarEventType[];
}> {
  return axios.get(`/api/v1/scheduler/org_event_types`);
}

export function getSchedulerOrgCalendarEventType(
  id: number,
): Promise<{
  data: SchedulerCalendarEventType;
}> {
  return axios.get(`/api/v1/scheduler/org_event_types/${id}`);
}

type GetSchedulerCalendarEventTypesFilters = {
  active: boolean;
  userId__in?: number[];
  urlSlug?: string;
};

type GetSchedulerCalendarEventTypesParams = {
  userIds?: number[] | null;
  urlSlug?: string;
};

export function getSchedulerCalendarEventTypes(
  p: GetSchedulerCalendarEventTypesParams,
): Promise<{
  data: SchedulerCalendarEventType[];
}> {
  const filters = { active: true } as GetSchedulerCalendarEventTypesFilters;
  if (p.userIds && p.userIds.length) {
    filters['userId__in'] = p.userIds;
  }

  if (p.urlSlug) {
    filters['urlSlug'] = p.urlSlug;
  }

  const params = {
    filters: [JSON.stringify(filters)],
  };
  return axios.get(`/api/v1/scheduler/event_types`, { params });
}

export function getSchedulerCalendarEventType(
  id: number,
): Promise<{
  data: SchedulerCalendarEventType;
}> {
  return axios.get(`/api/v1/scheduler/event_types/${id}`);
}

export function deleteSchedulerCalendarEventType(id: number): Promise<void> {
  return axios.delete(`/api/v1/scheduler/event_types/${id}`);
}

export function getSchedulerAgentMeetPage(): Promise<{
  data: SchedulerAgentMeetPage;
}> {
  return axios.get(`/api/v1/scheduler/agent_meet_page`);
}

export function patchSchedulerAgentMeetPage(
  data: SchedulerAgentMeetPage,
): Promise<{
  data: SchedulerAgentMeetPage;
}> {
  // Only ever send `imageId` strings, never `MunchedImage` objects
  delete data.bannerImage;
  delete data.instantMeetingImage;
  delete data.profileImage;
  if (data.testimonials.length > 0) {
    data.testimonials.forEach(t => {
      delete t.logoImage;
    });
  }
  return axios.patch(`/api/v1/scheduler/agent_meet_page`, data);
}

export function getSchedulerPublicAgentMeetPage(params: {
  urlSlug?: string;
  userId?: number;
}): Promise<{
  data: SchedulerPublicAgentMeetPage[];
}> {
  const filters: Record<string, any> = {};
  if (params.urlSlug) {
    filters['urlSlug'] = params.urlSlug;
  }
  if (params.userId) {
    filters['userId'] = params.userId;
  }
  return axios.get(
    `/api/v1/public/scheduler/agent_meet_page?filters=${JSON.stringify(filters)}`,
  );
}

export function getSchedulerPublicEvent(params: {
  eventId: string;
}): Promise<{
  data: SchedulerCalendarPublicEvent;
}> {
  return axios.get(`/api/v1/public/scheduler/events/${params.eventId}`);
}

export function getSchedulerPublicEventStartTimes(
  eventTypeId: number,
  startTime: string,
  endTime: string,
  userId?: number,
): Promise<{ data: string[] }> {
  const url = `/api/v1/public/scheduler/event_types/${eventTypeId}/start_times`;
  const params: Record<string, any> = { startTime, endTime };
  if (userId) {
    params['userId'] = userId;
  }
  return axios.get(url, { params: params });
}

export function getSchedulerPublicCalendarEventTypes(
  p: GetSchedulerCalendarEventTypesParams,
): Promise<{
  data: SchedulerCalendarPublicEvent[];
}> {
  const filters = { active: true } as GetSchedulerCalendarEventTypesFilters;
  if (p.userIds && p.userIds.length) {
    filters['userId__in'] = p.userIds;
  }

  if (p.urlSlug) {
    filters['urlSlug'] = p.urlSlug;
  }

  const params = {
    filters: [JSON.stringify(filters)],
  };
  return axios.get(`/api/v1/public/scheduler/event_types`, { params });
}

export function postPublicSchedulerEvent(
  data: SchedulerCalendarEventAdd,
): Promise<{ data: SchedulerCalendarPublicEvent }> {
  return axiosWrapper(axios.post(`/api/v1/public/scheduler/events`, data));
}

export function updatePublicSchedulerEvent(
  id: string,
  data: { startTime: string },
): Promise<{ data: SchedulerCalendarPublicEvent }> {
  return axios.put(`/api/v1/public/scheduler/events/${id}`, data);
}

export function cancelPublicSchedulerEvent(
  id: string,
): Promise<{ data: SchedulerCalendarPublicEvent }> {
  return axios.delete(`/api/v1/public/scheduler/events/${id}`);
}

/**
 * Little Black Book CRM
 */

export function getLbbStats(): Promise<{
  data: LBBStats;
}> {
  return axios.get(`/api/v1/little_black_book/stats`);
}

interface GetLbbContactsParams extends PageParams {
  stage?: string | null;
  source?: string | null;
  source__in?: string[] | null;
  email?: string | null;
}

export function getLbbContacts(
  params: GetLbbContactsParams,
): Promise<{
  data: Page<LBBContact>;
}> {
  params.filters = [
    JSON.stringify({
      active: true,
      stage: params.stage,
      source: params.source,
      source__in: params.source__in,
    }),
  ];
  if (params.sort?.includes('alert__')) {
    params.sort = params.sort.replace('alert__', 'alert.contact_id__');
  }
  return axios.get(`/api/v1/little_black_book/contacts`, { params });
}

export function postLBBContactsAlert(contactIds: number[]): Promise<{ data: void }> {
  return axios.post(`/api/v1/little_black_book/bulk_contact_alerts`, { contactIds });
}

export function deleteLBBContactsAlert(contactIds: number[]): Promise<{ data: void }> {
  return axios.delete(`/api/v1/little_black_book/bulk_contact_alerts`, {
    data: { contactIds },
  });
}

export function getLbbDeals(): Promise<{ data: LBBDeal[] }> {
  return axios.get(`/api/v1/little_black_book/deals`);
}

export function getLbbCompanies(
  params: PageParams,
): Promise<{
  data: Page<LBBCompany>;
}> {
  if (!params.filters) {
    params.filters = [JSON.stringify({ active: true })];
  }
  return axios.get(`/api/v1/little_black_book/companies`, { params });
}

export function getLbbCompanyOwners(): Promise<{ data: LBBCompanyOwner[] }> {
  return axios.get(`/api/v1/little_black_book/company_owners`);
}

export function getSpotlightCompaniesCsv(): Promise<{ data: string }> {
  return axios.get(`/api/v1/little_black_book/companies/spotlight_export`);
}

export function syncLbb(
  service: OrgIntegrationService,
): Promise<{
  data: void;
}> {
  return axios.post(`/api/v1/little_black_book/external/import`, { service });
}

export function getCustomMetaDataLeadFields(): Promise<{
  data: CustomMetadataField[];
}> {
  return axios.get(`/api/v1/little_black_book/custom_metadata/lead_fields`);
}

export function getCustomMetaDataContactFields(): Promise<{
  data: CustomMetadataField[];
}> {
  return axios.get(`/api/v1/little_black_book/custom_metadata/contact_fields`);
}

export function getCustomMetaDataFieldDependencies(
  id: number,
  objectType: 'contact' | 'company' | 'deal',
): Promise<{
  data: CustomMetadataFieldDependentEntity[];
}> {
  return axios.get(
    `/api/v1/little_black_book/custom_metadata/${objectType}_fields/${id}/dependent_entities`,
  );
}

export function deleteCustomMetaDataField(
  id: number,
  objectType: 'contact' | 'company' | 'deal',
): Promise<void> {
  return axios.delete(
    `/api/v1/little_black_book/custom_metadata/${objectType}_fields/${id}`,
  );
}

export function postCustomMetaDataContactField(
  newField: Omit<CustomMetadataField, 'id'>,
): Promise<{ data: CustomMetadataField }> {
  return axios.post(`/api/v1/little_black_book/custom_metadata/contact_fields`, newField);
}

export function getCustomMetaDataContactFieldsMapping(): Promise<{ data: any }> {
  return axios.get(`/api/v1/little_black_book/custom_metadata/contact_field_mappings`);
}

export function getCustomMetaDataCompanyFields(): Promise<{ data: any }> {
  return axios.get(`/api/v1/little_black_book/custom_metadata/company_fields`);
}

export function getCustomMetaDataCompanyFieldsMapping(): Promise<{ data: any }> {
  return axios.get(`/api/v1/little_black_book/custom_metadata/company_field_mappings`);
}

export function getCustomMetaDataDealFields(): Promise<{ data: any }> {
  return axios.get(`/api/v1/little_black_book/custom_metadata/deal_fields`);
}

export function getCustomMetaDataDealFieldsMapping(): Promise<{ data: any }> {
  return axios.get(`/api/v1/little_black_book/custom_metadata/deal_field_mappings`);
}

export function postCustomMetaDataContactFieldsMapping(
  mappings: any,
): Promise<{ data: any }> {
  return axios.post(`/api/v1/little_black_book/custom_metadata/contact_field_mappings`, {
    ...mappings,
  });
}

export function postCustomMetaDataCompanyFieldsMapping(
  mappings: any,
): Promise<{ data: any }> {
  return axios.post(`/api/v1/little_black_book/custom_metadata/company_field_mappings`, {
    ...mappings,
  });
}

export function postCustomMetaDataDealFieldsMapping(
  mappings: any,
): Promise<{ data: any }> {
  return axios.post(`/api/v1/little_black_book/custom_metadata/deal_field_mappings`, {
    ...mappings,
  });
}

export function updateCustomMetaDataContactFieldsMapping(
  mappings: any,
  externalPropertyId: number,
): Promise<{ data: any }> {
  return axios.put(
    `/api/v1/little_black_book/custom_metadata/contact_field_mappings/${externalPropertyId}`,
    {
      ...mappings,
    },
  );
}

export function updateCustomMetaDataCompanyFieldsMapping(
  mappings: any,
  externalPropertyId: number,
): Promise<{ data: any }> {
  return axios.put(
    `/api/v1/little_black_book/custom_metadata/company_field_mappings/${externalPropertyId}`,
    {
      ...mappings,
    },
  );
}

export function updateCustomMetaDataDealFieldsMapping(
  mappings: any,
  externalPropertyId: number,
): Promise<{ data: any }> {
  return axios.put(
    `/api/v1/little_black_book/custom_metadata/deal_field_mappings/${externalPropertyId}`,
    {
      ...mappings,
    },
  );
}

export function postHubspotBatchMapping(mappings: any): Promise<{ data: any }> {
  return axios.post(`/api/v1/little_black_book/custom_metadata/batch_hubspot_mapping`, {
    ...mappings,
  });
}

export function postOutreachBatchMapping(mappings: any): Promise<{ data: any }> {
  return axios.post(`/api/v1/little_black_book/custom_metadata/batch_outreach_mapping`, {
    ...mappings,
  });
}

export function postSalesforceBatchMapping(mappings: any): Promise<{ data: any }> {
  return axios.post(
    `/api/v1/little_black_book/custom_metadata/batch_salesforce_mapping`,
    {
      ...mappings,
    },
  );
}

export function getContactPipelines(service?: string): Promise<{ data: any }> {
  const params: Record<string, string> = {};
  if (service) {
    params.service = service;
  }

  return axios.get(`/api/v1/little_black_book/contacts/pipelines`, {
    params,
  });
}

/**
 * Live Visitor Feeds // Streams
 */

export function getVisitorFeeds(): Promise<{
  data: any;
}> {
  return axios.get(`/api/v1/streams`);
}

export function deleteVisitorFeed(id: number): Promise<void> {
  return axios.delete(`/api/v1/streams/${id}`);
}

export function postVisitorFeed(data: NewVisitorFeed): Promise<{ data: VisitorFeed }> {
  return axios.post(`/api/v1/streams`, data);
}

export function patchVisitorFeed(
  id: number,
  data: NewVisitorFeed,
): Promise<{ data: VisitorFeed }> {
  return axios.patch(`/api/v1/streams/${id}`, data);
}

export function putVisitorFeedOrder(
  orderedIds: number[],
): Promise<{ data: VisitorFeed[] }> {
  return axios.put(`/api/v1/streams/priority`, { order: orderedIds });
}

/**
 * Reusable abstractions
 */

export function getListAny<T>(path: string): Promise<{ data: T[] }> {
  return axios.get(`/api/v1/${path}`);
}

export function getOneAny<T>(path: string, id: number | string): Promise<{ data: T }> {
  return axios.get(`/api/v1/${path}/${id}`);
}

export function postAny<T>(path: string, data: object): Promise<{ data: T }> {
  return axios.post(`/api/v1/${path}`, data);
}

export function patchAny<T>(
  path: string,
  id: number,
  data: object,
): Promise<{ data: T }> {
  return axios.patch(`/api/v1/${path}/${id}`, data);
}

export function putReorderAny(
  path: string,
  orderedIds: number[],
): Promise<{ data: void }> {
  return axios.put(`/api/v1/${path}/reorder`, { ids: orderedIds });
}

export function deleteAny(path: string, id: number): Promise<{ data: void }> {
  return axios.delete(`/api/v1/${path}/${id}`);
}

/**
 * Outbound Api Methods
 */

export function getOutboundOnboardingStatus(): Promise<{ data: OnboardingStatus }> {
  return axios.get('/api/v1/outbound/onboarding/status');
}

export function postTwilioTrusthubRegistration(
  args: TrusthubRegistrationBody,
): Promise<{ data: OnboardingStatus }> {
  return axios.post(`/api/v1/outbound/twilio/register_trusthub`, args);
}

export function getTwilioToken(): Promise<{ data: TwilioTokenResponse }> {
  return axios.get(`/api/v1/outbound/voice_token`);
}

export function getApolloCallTasks(): Promise<{ data: any }> {
  return axios.get(`/api/v1/outbound/call_tasks/apollo`);
}

export function getHubspotCallTasks(): Promise<{ data: any }> {
  return axios.get(`/api/v1/outbound/call_tasks/hubspot`);
}

export function getOutreachCallTasks(): Promise<{ data: any }> {
  return axios.get(`/api/v1/outbound/call_tasks/outreach`);
}

export function getSalesloftCallTasks(): Promise<{ data: any }> {
  return axios.get(`/api/v1/outbound/call_tasks/salesloft`);
}

export function getDebugCallTasks(): Promise<{ data: any }> {
  return axios.get(`/api/v1/outbound/call_tasks/debug`);
}
export function getHubspotContactListCallTasks(
  listId: number,
  cursor: number | undefined,
): Promise<{ data: ContactListCallTasksResponse }> {
  return axios.get(`/api/v1/outbound/dial_lists/hubspot/${listId}/call_tasks`, {
    params: {
      cursor,
    },
  });
}

export function getHubspotContactLists(): Promise<{ data: ContactList[] }> {
  return axios.get(`/api/v1/outbound/dial_lists/hubspot`);
}

export function getHubspotCallDispositions(): Promise<{
  data: CallDisposition[];
}> {
  return axios.get(`/api/v1/outbound/dispositions/hubspot`);
}

export function getOutreachCallDispositions(): Promise<{
  data: CallDisposition[];
}> {
  return axios.get(`/api/v1/outbound/dispositions/outreach`);
}

export function getOutreachContactLists(): Promise<{ data: ContactList[] }> {
  return axios.get(`/api/v1/outbound/dial_lists/outreach`);
}

export function getOutreachContactListCallTasks(
  listId: number,
  cursor: number | undefined,
): Promise<{ data: ContactListCallTasksResponse }> {
  return axios.get(`/api/v1/outbound/dial_lists/outreach/${listId}/call_tasks`, {
    params: {
      cursor,
    },
  });
}

export function getSalesloftSettings(): Promise<{ data: SalesloftSettings }> {
  return axios.get(`/api/v1/integrations/org/salesloft/settings`);
}

export function patchSalesloftSettings(
  data: Partial<SalesloftSettings>,
): Promise<{ data: SalesloftSettings }> {
  return axios.patch(`/api/v1/integrations/org/salesloft/settings`, data);
}

export function getSalesloftCallDispositions(): Promise<{
  data: CallDisposition[];
}> {
  return axios.get(`/api/v1/outbound/dispositions/salesloft`);
}

export function getSalesloftCallSentiments(): Promise<{
  data: CallSentiment[];
}> {
  return axios.get(`/api/v1/outbound/sentiments/salesloft`);
}

export function getSalesloftContactLists(): Promise<{ data: ContactList[] }> {
  return axios.get(`/api/v1/outbound/dial_lists/salesloft`);
}

export function getSalesloftContactListCallTasks(
  listId: number,
  cursor: number | undefined,
): Promise<{ data: ContactListCallTasksResponse }> {
  return axios.get(`/api/v1/outbound/dial_lists/salesloft/${listId}/call_tasks`, {
    params: {
      cursor,
    },
  });
}

export function getSalesforceCallDispositions(): Promise<{
  data: CallDisposition[];
}> {
  return axios.get(`/api/v1/outbound/dispositions/salesforce`);
}

export function getSalesforceContactLists(): Promise<{ data: ContactList[] }> {
  return axios.get(`/api/v1/outbound/dial_lists/salesforce`);
}

export function getSalesforceContactListCallTasks(
  listId: number,
  cursor: number | undefined,
): Promise<{ data: ContactListCallTasksResponse }> {
  return axios.get(`/api/v1/outbound/dial_lists/salesforce/${listId}/call_tasks`, {
    params: {
      cursor,
    },
  });
}

export function getServicebellCallDispositions(): Promise<{
  data: CallDisposition[];
}> {
  return axios.get(`/api/v1/outbound/dispositions/servicebell`);
}

export function getServicebellContactLists(): Promise<{ data: ContactList[] }> {
  return axios.get(`/api/v1/outbound/dial_lists/servicebell`);
}

export function deleteServicebellContactList(listId: number): Promise<{ data: {} }> {
  return axios.delete(`/api/v1/outbound/dial_lists/servicebell/${listId}`);
}

export function getServicebellContactListCallTasks(
  listId: number,
  cursor: number | undefined,
): Promise<{ data: ContactListCallTasksResponse }> {
  return axios.get(`/api/v1/outbound/dial_lists/servicebell/${listId}/call_tasks`, {
    params: {
      cursor: cursor,
    },
  });
}

export function getSalesforceContactListSchema(
  listId: number,
): Promise<{ data: ContactListSchema }> {
  return axios.get(`/api/v1/outbound/dial_lists/salesforce/${listId}/schema`);
}

export function getApolloCallDispositions(): Promise<{
  data: CallDisposition[];
}> {
  return axios.get(`/api/v1/outbound/dispositions/apollo`);
}

export function getApolloContactLists(): Promise<{ data: ContactList[] }> {
  return axios.get(`/api/v1/outbound/dial_lists/apollo`);
}

export function getApolloContactListCallTasks(
  listId: number,
  cursor: number | undefined,
): Promise<{ data: ContactListCallTasksResponse }> {
  return axios.get(`/api/v1/outbound/dial_lists/apollo/${listId}/call_tasks`, {
    params: {
      cursor,
    },
  });
}

export function resetApolloContactListCallTasks(
  listId: number,
): Promise<{ data: ContactListCallTasksResponse }> {
  return axios.get(`/api/v1/outbound/dial_lists/apollo/${listId}/reset`);
}

export function resetOutreachContactListCallTasks(
  listId: number,
): Promise<{ data: ContactListCallTasksResponse }> {
  return axios.get(`/api/v1/outbound/dial_lists/outreach/${listId}/reset`);
}

export function resetSalesforceContactListCallTasks(
  listId: number,
): Promise<{ data: ContactListCallTasksResponse }> {
  return axios.get(`/api/v1/outbound/dial_lists/salesforce/${listId}/reset`);
}

export function resetHubspotContactListCallTasks(
  listId: number,
): Promise<{ data: ContactListCallTasksResponse }> {
  return axios.get(`/api/v1/outbound/dial_lists/hubspot/${listId}/reset`);
}

export function resetServicebellContactListCallTasks(
  listId: number,
): Promise<{ data: ContactListCallTasksResponse }> {
  return axios.get(`/api/v1/outbound/dial_lists/servicebell/${listId}/reset`);
}

export function getPurchasedPhoneNumbers(): Promise<{ data: PhoneNumber[] }> {
  return axios.get(`/api/v1/outbound/purchased_phone_numbers`);
}

export function getAvailablePhoneNumbers(
  areaCode?: string,
): Promise<{ data: TwilioPhoneNumber[] }> {
  const queryString = areaCode ? `?area_code=${areaCode}` : '';
  return axios.get(`/api/v1/outbound/twilio/available_phone_numbers${queryString}`);
}

export function postPurchasePhoneNumber(phoneNumber: string): Promise<{ data: void }> {
  return axios.post(`/api/v1/outbound/twilio/purchase_phone_number`, { phoneNumber });
}

export function releasePhoneNumber(phoneNumberSid: string): Promise<{ data: void }> {
  return axios.delete(`/api/v1/outbound/purchased_phone_numbers/${phoneNumberSid}`);
}

export interface DialSessionCreateArgs {
  selectedNumber: string;
  sourceType: SourceType;
  startingTaskCount: number;
  sourceId: number | null;
  preRecordedVoicemailId?: number | null;
}

export function postDialSession(
  args: DialSessionCreateArgs,
): Promise<{ data: DialSession }> {
  return axios.post(`/api/v1/outbound/dial_session`, args);
}

export function postCloseDialSession(
  dialSessionId: number,
): Promise<{ data: DialSession }> {
  return axios.post(`/api/v1/outbound/dial_session/${dialSessionId}/close_session`);
}

export interface DialTaskCreateArgs {
  callTask: CallTask;
  selectedNumber: string;
  sourceType: string;
  amdMode: 'OFF' | 'FAST' | 'SLOW';
}

export function postDialTask(
  dialSessionId: number,
  args: DialTaskCreateArgs,
): Promise<{ data: DialTask }> {
  return axios.post(`/api/v1/outbound/dial_session/${dialSessionId}/task`, args);
}

export function postCloseDialTask(
  dialSessionId: number,
  dialTaskCallSid: string,
  wasAnswered = false,
): Promise<{ data: DialTask }> {
  return axios.post(`/api/v1/outbound/dial_session/${dialSessionId}/close_task`, {
    taskCallSid: dialTaskCallSid,
    wasAnswered,
  });
}

export function postRetryDialTask(
  dialSessionId: number,
  dialTaskCallSid: string,
  amdMode: 'OFF' | 'FAST' | 'SLOW',
): Promise<{ data: DialTask }> {
  return axios.post(`/api/v1/outbound/dial_session/${dialSessionId}/retry_task`, {
    taskCallSid: dialTaskCallSid,
    amdMode,
  });
}

export function postMarkDialTaskNumberInvalid(
  dialSessionId: number,
  dialTaskCallSid: string,
  invalidNumber: string,
): Promise<{ data: DialTask }> {
  return axios.post(
    `/api/v1/outbound/dial_session/${dialSessionId}/mark_task_number_invalid`,
    {
      taskCallSid: dialTaskCallSid,
      invalidNumber,
    },
  );
}

export interface PostFinalCallStatusArgs {
  notes: string;
  dispositionId: string;
  sentimentId: string | null;
}

export function postFinalCallReport(
  dialTaskId: string,
  args: PostFinalCallStatusArgs,
): Promise<{ data: any }> {
  // URL will likely change when we switch backend to a session/task nested crudmethodview setup
  return axios.post(`/api/v1/outbound/dial_task/${dialTaskId}/call_report`, args);
}

export function patchCallbackNumber(
  callbackNumber: string | undefined,
): Promise<{ data: any }> {
  return axios.patch(`/api/v1/outbound/callback_number`, {
    callbackNumber: callbackNumber || '',
  });
}

export function getOutboundCallReports({
  startDate,
  endDate,
  page,
  perPage = 10,
  search,
  users,
  dispositions,
  sources,
  companyId,
  contactId,
}: {
  startDate: string;
  endDate: string;
  page: number;
  perPage?: number;
  search?: string;
  users?: string[];
  dispositions?: string[];
  sources?: string[];
  companyId?: number | null;
  contactId?: number | null;
}): Promise<{ data: { total: number; logs: OutboundCallReport[] } }> {
  const [sourceTypes, _sources] =
    sources?.reduce(
      (acc, s) => {
        if (
          [
            SourceType.HUBSPOT_TASK,
            SourceType.OUTREACH_TASK,
            SourceType.DIALPAD,
          ].includes(s as SourceType)
        ) {
          acc[0].push(s);
        } else {
          acc[1].push(s);
        }
        return acc;
      },
      [[] as string[], [] as string[]],
    ) ?? [];

  return axios.get(`/api/v1/outbound/call_reports`, {
    params: {
      startDate,
      endDate,
      page,
      perPage,
      users,
      dispositions,
      sources: _sources,
      sourceTypes,
      search,
      companyId,
      contactId,
    },
  });
}

export function getOutboundContactCallReports(
  contactId: number,
  page = 1,
  perPage = 20,
): Promise<{ data: OutboundCallReport[] }> {
  return axios.get(
    `/api/v1/outbound/call_reports/${contactId}?page=${page}&perPage=${perPage}`,
  );
}

export function getOutboundContactsLastCallReport(
  contactIds: number[],
): Promise<{ data: OutboundCallReport[] }> {
  return axios.get(`/api/v1/outbound/last_call_report`, {
    params: { contactIds },
  });
}

export function getOutboundCallSingleReport(
  dial_task_id: string,
): Promise<{ data: OutboundCallReport }> {
  return axios.get(`/api/v1/outbound/call_reports/${dial_task_id}`);
}

export function getDialTaskRecording(dialTaskId: string): Promise<{ data: any }> {
  return axios.get(`/api/v1/outbound/dial_task/${dialTaskId}/recording`);
}

export function getDialTask(dialTaskId: string): Promise<{ data: DialTask }> {
  return axios.get(`/api/v1/outbound/dial_task/${dialTaskId}`);
}

export function postOutboundHandshakeReply(
  dialTaskId: string,
  token: string,
  status: 'ok' | 'hang-up',
): Promise<{ data: any }> {
  return axios.post(`/api/v1/outbound/handshake`, {
    dialTaskId,
    token,
    status,
  });
}

export async function getDialTaskTranscript(
  id: string,
): Promise<{ data: OutboundTranscription }> {
  return axios.get(`/api/v1/outbound/dial_task/${id}/transcript`);
}

export function uploadOutboundVoicemailRecording({
  audioBlob,
  title,
}: {
  audioBlob: Blob;
  title?: string;
}): Promise<{ data: OutboundVoicemailRecording }> {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'audio');

  if (title) {
    formData.append('title', title);
  }

  return axios.post(`api/v1/outbound/voicemails`, formData);
}

export function getOutboundVoicemailRecordings(): Promise<{
  data: OutboundVoicemailRecording[];
}> {
  return axios.get(`api/v1/outbound/voicemails`);
}

export function deleteOutboundVoicemailRecording(
  id: number,
): Promise<{ data: OutboundVoicemailRecording }> {
  return axios.delete(`api/v1/outbound/voicemails/${id}`);
}

export function updateOutboundVoicemailRecording(
  id: number,
  title: string,
): Promise<{ data: OutboundVoicemailRecording }> {
  return axios.patch(`api/v1/outbound/voicemails/${id}`, {
    title,
  });
}

/**
 * Video Api Methods
 */
export function postVideoBackground(imageId: string): Promise<{ data: VideoBackground }> {
  return axios.post(`/api/v1/video/backgrounds`, { imageId });
}

export function getVideoBackgrounds(): Promise<{ data: VideoBackground[] }> {
  return axios.get(`/api/v1/video/backgrounds`);
}

export function deleteVideoBackground(id: string): Promise<void> {
  return axios.delete(`/api/v1/video/backgrounds/${id}`);
}

/**
 * Contact, Companies, Deals Properties Api Methods
 */

export function getAllProperties(): Promise<{ data: AllProperties }> {
  return axios.get(`/api/v1/little_black_book/custom_metadata`);
}

export function getContactProperties(): Promise<{ data: Property[] }> {
  return axios.get(`/api/v1/little_black_book/custom_metadata/contact_fields`);
}

export function getCompanyProperties(): Promise<{ data: any[] }> {
  return axios.get(`/api/v1/little_black_book/custom_metadata/company_fields`);
}

export function getDealProperties(): Promise<{ data: any[] }> {
  return axios.get(`/api/v1/little_black_book/custom_metadata/deal_fields`);
}

export function getDefaultDescriptionForKey(
  key: string,
): Promise<{ data: { property: string } }> {
  return axios.get(`/api/v1/ai-chatbots/metadata/${key}/description`);
}

export function searchLBBContacts(term: string): Promise<{ data: any[] }> {
  return axios.get(`/api/v1/little_black_book/contacts?search=${term}`);
}

export function postContactProperty(
  property: Omit<Property, 'id'>,
): Promise<{ data: Property }> {
  return axios.post(`/api/v1/little_black_book/custom_metadata/contact_fields`, property);
}

export function postCompanyProperty(property: any): Promise<{ data: Property }> {
  return axios.post(`/api/v1/little_black_book/custom_metadata/company_fields`, property);
}

export function postDealProperty(property: any): Promise<{ data: Property }> {
  return axios.post(`/api/v1/little_black_book/custom_metadata/deal_fields`, property);
}

export function updateContactProperty(
  property: Pick<Property, 'id' | 'displayName' | 'description'>,
): Promise<{ data: Property }> {
  return axios.put(
    `/api/v1/little_black_book/custom_metadata/contact_fields/${property.id}`,
    {
      displayName: property.displayName,
      description: property.description,
    },
  );
}

export function updateCompanyProperty(
  property: Pick<Property, 'id' | 'displayName' | 'description'>,
): Promise<{ data: Property }> {
  return axios.put(
    `/api/v1/little_black_book/custom_metadata/company_fields/${property.id}`,
    {
      displayName: property.displayName,
      description: property.description,
    },
  );
}

export function updateDealProperty(
  property: Pick<Property, 'id' | 'displayName' | 'description'>,
): Promise<{ data: Property }> {
  return axios.put(
    `/api/v1/little_black_book/custom_metadata/deal_fields/${property.id}`,
    {
      displayName: property.displayName,
      description: property.description,
    },
  );
}

interface AppNotificationFiltersParams {
  dateCreated__lt?: string | null;
}

export function getAppNotifications({
  page,
  dateCreated,
  pageSize = 50,
}: {
  page: number;
  dateCreated?: string;
  pageSize?: number;
}): Promise<{ data: Page<AppNotification> }> {
  const params = { filters: [], page, pageSize } as PageParams;
  const filters = {} as AppNotificationFiltersParams;
  params.sort = 'dateCreated__desc';
  if (dateCreated) {
    filters.dateCreated__lt = new Date(dateCreated).toISOString();
  }

  params.filters = [JSON.stringify(filters)];
  return axios.get(`/api/v1/app_notifications/mine`, { params });
}

export function deleteAppNotification(args: { id: number }): Promise<void> {
  return axios.delete(`/api/v1/app_notifications/mine/${args.id}`);
}

export function putAppNotificationRead(
  appNotificationId: AppNotification['id'],
): Promise<{ data: void }> {
  return axios.put(`/api/v1/app_notifications/mine/${appNotificationId}/read`);
}

export function putAppNotificationReadAll(): Promise<{ data: void }> {
  return axios.put(`/api/v1/app_notifications/mine/read_all`);
}

export function addContact(
  contact: Omit<LBBContact, 'id'>,
): Promise<{ data: LBBContact }> {
  return axios.post(`/api/v1/visitor/contact`, contact);
}

export function deleteLbbContactPhoneNumber(
  id: number,
  number: string,
): Promise<{ data: LBBContact }> {
  return axios.delete(`/api/v1/little_black_book/contacts/${id}/phone_numbers`, {
    data: { number },
  });
}

export function updateLbbContact(
  id: number,
  contact: Partial<LBBContact>,
): Promise<{ data: LBBContact }> {
  return axios.patch(`/api/v1/little_black_book/contacts/${id}`, contact);
}

export function createLbbContact(
  contact: LBBCreateContact,
): Promise<{ data: LBBContact }> {
  return axios.post(`/api/v1/little_black_book/contacts`, contact);
}

export function createLbbCompany(
  company: Omit<Partial<LBBCompany>, 'id'>,
): Promise<{ data: LBBCompany }> {
  return axios.post(`/api/v1/little_black_book/companies`, company);
}

export function updateLbbCompany(
  id: number,
  company: Partial<LBBCompany>,
): Promise<{ data: LBBCompany }> {
  return axios.patch(`/api/v1/little_black_book/companies/${id}`, company);
}

export function postCallLog(args: { callLog: CallLog }): Promise<{ data: any }> {
  return axios.post(`/api/v1/call_log`, args.callLog);
}

export function uploadAiVoiceSample(args: { audioBlob: Blob }): Promise<{ data: any }> {
  const formData = new FormData();
  formData.append('audio', args.audioBlob, 'audio');
  return axios.post(`api/v1/ai_voice/sample`, formData);
}

export function getAiVoiceTts(text: string): Promise<{ data: Blob }> {
  return axios.post(`/api/v1/ai_voice/tts`, { text }, { responseType: 'blob' });
}

export function getAiVoice(): Promise<{ data: AiVoice }> {
  return axios.get(`/api/v1/ai_voice`);
}

export function getAiVoiceScripts(): Promise<{ data: AiVoiceScript[] }> {
  return axios.get(`/api/v1/ai_voice/script`);
}

export function putAiVoiceScript(
  script: AiVoiceScriptUpdate,
): Promise<{ data: AiVoiceScript }> {
  return axios.put(`/api/v1/ai_voice/script/${script.id}`, { ...script, id: undefined });
}

export function postAiVoiceScript(
  script: AiVoiceScriptCreate,
): Promise<{ data: AiVoiceScript }> {
  return axios.post(`/api/v1/ai_voice/script`, script);
}

export function deleteAiVoiceScript(script: Pick<AiVoiceScript, 'id'>): Promise<void> {
  return axios.delete(`/api/v1/ai_voice/script/${script.id}`);
}

export function fetchSidepanelPropertyGroups(): Promise<{
  data: SidepanelPropertyGroup[];
}> {
  return axios.get(`/api/v1/ui_config/sidepanel_property_groups`);
}

export function patchSidepanelProperty(
  id: number,
  property: Partial<SidepanelProperty>,
): Promise<{ data: SidepanelProperty }> {
  return axios.patch(`/api/v1/ui_config/sidepanel_properties/${id}`, property);
}

export function putReorderSidepanelProperties(
  orderedIds: number[],
  type: string,
): Promise<{ data: void }> {
  return axios.put(`/api/v1/ui_config/sidepanel_properties/reorder`, {
    ids: orderedIds,
    type: type,
  });
}

export function getSpotlightAccount(
  accountId: number,
): Promise<{ data: SpotlightAccount }> {
  return axios.get(`/api/v1/spotlight/accounts/${accountId}`);
}

export function getSpotlightAccountUTMs(
  accountId: number,
  page = 1,
): Promise<{ data: SpotlightUTMs }> {
  return axios.get(`/api/v1/spotlight/accounts/${accountId}/utms?page=${page}`);
}

export function getSpotlightAccountUTM(
  accountId: number,
  utm: SpotlightUTM,
  page = 1,
): Promise<{ data: SpotlightUTMs }> {
  return axios.get(`/api/v1/spotlight/accounts/${accountId}/utms/${utm}?page=${page}`);
}

export function getSpotlightAccountActivities(
  accountId: number,
  page?: number,
): Promise<{ data: VisitorActivityResponse }> {
  return axios.get(`/api/v1/spotlight/accounts/${accountId}/activities`, {
    params: { page: page || 1 },
  });
}

export function getSpotlightAccountFilters(): Promise<{ data: SpotlightFilterMeta[] }> {
  return axios.get(`/api/v1/spotlight/accounts/filters`);
}

export function searchSpotlightAccounts(
  filters: SpotlightFilter[],
  page?: number,
  perPage?: number,
  sort?: string,
): Promise<{ data: SpotlightAccountsResponse }> {
  const timeoutSeconds = 60;
  return axios.post(
    `/api/v1/spotlight/accounts/search`,
    {
      filters,
      sort,
      page: page || 1,
      perPage: perPage || 100,
    },
    {
      timeout: timeoutSeconds * 1000,
      headers: {
        [REQUEST_TIMEOUT_HEADER]: timeoutSeconds,
      },
    },
  );
}

export function searchSpotlightAccountsIds(
  filters: SpotlightFilter[],
): Promise<{ data: SpotlightAccountsIdsResponse }> {
  return axios.post(`/api/v1/spotlight/accounts/ids`, {
    filters,
  });
}

export function searchSpotlightAccountsCSV(
  filters: SpotlightFilter[],
  sort?: string,
): Promise<{ data: string }> {
  return axios.post(`/api/v1/spotlight/accounts/search.csv`, {
    filters,
    sort,
  });
}

export function searchSpotlightAccountsActivityCSV(
  filters: SpotlightFilter[],
  sort?: string,
): Promise<{ data: string }> {
  return axios.post(`/api/v1/spotlight/accounts/activity.csv`, {
    filters,
    sort,
  });
}

export function getSpotlightAccountLists(
  page?: number,
  perPage?: number,
): Promise<{
  data: {
    items: SpotlightAccountList[];
  };
}> {
  return axios.get(`/api/v1/spotlight/account_lists`, {
    params: { page: page || 1, perPage: perPage || 100 },
  });
}

export function createSpotlightAccountList(
  request: CreateSpotlightAccountListRequest,
): Promise<{ data: SpotlightAccountList }> {
  return axios.post(`/api/v1/spotlight/account_lists`, request);
}

export function deleteSpotlightAccountList(accountListId: number): Promise<{ data: {} }> {
  return axios.delete(`/api/v1/spotlight/account_lists/${accountListId}`);
}

export function updateSpotlightAccountList(
  accountListId: number,
  request: UpdateSpotlightAccountListRequest,
): Promise<{ data: SpotlightAccountList }> {
  return axios.patch(`/api/v1/spotlight/account_lists/${accountListId}`, request);
}

export function getSpotlightAccountList(
  accountListId: number,
): Promise<{ data: SpotlightAccountList }> {
  return axios.get(`/api/v1/spotlight/account_lists/${accountListId}`);
}

export function getSpotlightAccountListAccounts(
  accountListId: number,
  sort?: string,
  page?: number,
  perPage?: number,
): Promise<{ data: SpotlightAccountsResponse }> {
  return axios.get(`/api/v1/spotlight/account_lists/${accountListId}/accounts`, {
    params: { sort, page: page || 1, perPage: perPage || 100 },
  });
}

export function getSpotlightAccountListSettings(): Promise<{
  data: SpotlightAccountListUserSettings;
}> {
  return axios.get(`/api/v1/spotlight/account_lists/settings`);
}

export function getSpotlightAccountListAnalytics(
  accountListId: number,
): Promise<{ data: SpotlightAccountListAnalytics }> {
  return axios.get(`/api/v1/spotlight/account_lists/${accountListId}/analytics`);
}

export function updateSpotlightAccountListsSettings(
  settings: SpotlightAccountListUserSettings,
): Promise<{
  data: SpotlightAccountListUserSettings;
}> {
  return axios.patch(`/api/v1/spotlight/account_lists/settings`, settings);
}

export function getSpotlightAccountVisitors(
  accountId: number,
): Promise<{ data: UniqueVisitor[] }> {
  return axios.get(`/api/v1/spotlight/accounts/${accountId}/visitors`);
}

export function postBatchAssignSpotlightAccounts(
  assignments: { accountId: number; ownerId: number }[],
): Promise<void> {
  return axios.post(`/api/v1/spotlight/accounts/batch_assignments`, { assignments });
}

export function getSpotlightAccountListAccountsCSV(
  accountListId: number,
): Promise<{ data: string }> {
  return axios.get(`/api/v1/spotlight/account_lists/${accountListId}/accounts.csv`);
}

export function postBatchCreateSpotlightAccounts(
  accounts: SpotlightCreateAccountRequest[],
): Promise<{ data: SpotlightAccount[] }> {
  return axios.post(`/api/v1/spotlight/accounts/batch`, { accounts });
}

export function getSpotlightContact(
  contactId: number,
): Promise<{ data: SpotlightContact }> {
  return axios.get(`/api/v1/spotlight/contacts/${contactId}`);
}

export function getSpotlightContactUTMs(
  contactId: number,
  page = 1,
): Promise<{ data: SpotlightUTMs }> {
  return axios.get(`/api/v1/spotlight/contacts/${contactId}/utms?page=${page}`);
}

export function getSpotlightContactUTM(
  contactId: number,
  utm: SpotlightUTM,
  page = 1,
): Promise<{ data: SpotlightUTMs }> {
  return axios.get(`/api/v1/spotlight/contacts/${contactId}/utms/${utm}?page=${page}`);
}

export function getSpotlightContactFilters(): Promise<{ data: SpotlightFilterMeta[] }> {
  return axios.get(`/api/v1/spotlight/contacts/filters`);
}

export function searchSpotlightContacts(
  filters: SpotlightFilter[],
  page?: number,
  perPage?: number,
  sort?: string,
): Promise<{ data: SpotlightContactsResponse }> {
  const timeoutSeconds = 60;
  return axios.post(
    `/api/v1/spotlight/contacts/search`,
    {
      filters,
      sort,
      page: page || 1,
      perPage: perPage || 100,
    },
    {
      timeout: timeoutSeconds * 1000,
      headers: {
        [REQUEST_TIMEOUT_HEADER]: timeoutSeconds,
      },
    },
  );
}

export function searchSpotlightContactsIds(
  filters: SpotlightFilter[],
): Promise<{ data: SpotlightContactsIdsResponse }> {
  return axios.post(`/api/v1/spotlight/contacts/ids`, {
    filters,
  });
}

export function searchSpotlightContactsCSV(
  filters: SpotlightFilter[],
  sort?: string,
): Promise<{ data: string }> {
  return axios.post(`/api/v1/spotlight/contacts/search.csv`, {
    filters,
    sort,
  });
}

export function searchSpotlightContactsActivityCSV(
  filters: SpotlightFilter[],
  sort?: string,
): Promise<{ data: string }> {
  return axios.post(`/api/v1/spotlight/contacts/activity.csv`, {
    filters,
    sort,
  });
}

export function getSpotlightContactLists(
  page?: number,
  perPage?: number,
): Promise<{ data: { items: SpotlightContactList[] } }> {
  return axios.get(`/api/v1/spotlight/contact_lists`, {
    params: { page: page || 1, perPage: perPage || 100 },
  });
}

export function createSpotlightContactList(
  request: CreateSpotlightContactListRequest,
): Promise<{ data: SpotlightContactList }> {
  return axios.post(`/api/v1/spotlight/contact_lists`, request);
}

export function deleteSpotlightContactList(contactListId: number): Promise<{ data: {} }> {
  return axios.delete(`/api/v1/spotlight/contact_lists/${contactListId}`);
}

export function updateSpotlightContactList(
  contactListId: number,
  request: UpdateSpotlightContactListRequest,
): Promise<{ data: SpotlightContactList }> {
  return axios.patch(`/api/v1/spotlight/contact_lists/${contactListId}`, request);
}

export function getSpotlightContactList(
  contactListId: number,
): Promise<{ data: SpotlightContactList }> {
  return axios.get(`/api/v1/spotlight/contact_lists/${contactListId}`);
}

export function getSpotlightContactListContacts(
  contactListId: number,
  sort?: string,
  page?: number,
  perPage?: number,
): Promise<{ data: SpotlightContactsResponse }> {
  return axios.get(`/api/v1/spotlight/contact_lists/${contactListId}/contacts`, {
    params: { sort, page: page || 1, perPage: perPage || 100 },
  });
}

export function getSpotlightContactListContactsCSV(
  contactListId: number,
): Promise<{ data: string }> {
  return axios.get(`/api/v1/spotlight/contact_lists/${contactListId}/contacts.csv`);
}

export function getSpotlightContactListSettings(): Promise<{
  data: SpotlightContactListUserSettings;
}> {
  return axios.get(`/api/v1/spotlight/contact_lists/settings`);
}

export function updateSpotlightContactListsSettings(
  settings: SpotlightContactListUserSettings,
): Promise<{
  data: SpotlightContactListUserSettings;
}> {
  return axios.patch(`/api/v1/spotlight/contact_lists/settings`, settings);
}

export function postBatchCreateSpotlightContacts(
  contacts: SpotlightCreateContactRequest[],
): Promise<{ data: SpotlightContact[] }> {
  return axios.post(`/api/v1/spotlight/contacts/batch`, { contacts });
}

export function getSpotlightIntentSignals(): Promise<{ data: SpotlightIntentSignal[] }> {
  return axios.get(`/api/v1/spotlight/intent_signals`);
}

export function getSpotlightPreferences(): Promise<{ data: SpotlightPreferences }> {
  return axios.get(`/api/v1/spotlight/preferences`);
}

export function updateSpotlightPreferences(
  preferences: SpotlightPreferences,
): Promise<{ data: SpotlightPreferences }> {
  return axios.patch(`/api/v1/spotlight/preferences`, preferences);
}

export function createSpotlightIntentSignal(
  intentSignal: CreateSpotlightIntentSignalRequest,
): Promise<{ data: SpotlightIntentSignal }> {
  return axios.post(`/api/v1/spotlight/intent_signals`, intentSignal);
}

export function updateSpotlightIntentSignal(
  intentSignal: SpotlightIntentSignal,
): Promise<{ data: SpotlightIntentSignal }> {
  return axios.patch(`/api/v1/spotlight/intent_signals/${intentSignal.id}`, intentSignal);
}

export function deleteSpotlightIntentSignal(
  intentSignalId: number,
): Promise<{ data: {} }> {
  return axios.delete(`/api/v1/spotlight/intent_signals/${intentSignalId}`);
}

export function getSpotlightIntentSignalFilters(
  kind: SpotlightIntentSignalKind,
): Promise<{ data: SpotlightFilterMeta[] }> {
  return axios.get(`/api/v1/spotlight/intent_signals/filters?intent_signal_kind=${kind}`);
}

export function createCSVUploadURL(): Promise<{ data: S3UploadTicket }> {
  return axios.post(`/api/v1/spotlight/csv/uploads`);
}

export function getCSVFileSchema(
  kind: CSVUploadKind,
  csvFileUrl: string,
): Promise<{ data: CSVSchema }> {
  return axios.post(`/api/v1/spotlight/csv/mappings-schema`, { kind, csvFileUrl });
}

export function createCSVUpload(data: CreateCSVUpload): Promise<{ data: CSVUpload }> {
  return axios.post(`/api/v1/spotlight/csv`, data);
}

export function getCSVUpload(id: number): Promise<{ data: CSVUpload }> {
  return axios.get(`/api/v1/spotlight/csv/${id}`);
}

export async function createSalesfloorRoom(
  room: CreateSalesfloorRoomRequest,
): Promise<{ data: SalesfloorRoom }> {
  return axios.post(`/api/v1/salesfloor/rooms`, room);
}

export async function deleteSalesfloorRoom(roomId: string): Promise<{ data: void }> {
  return axios.delete(`/api/v1/salesfloor/rooms/${roomId}`);
}

export async function getSalesfloorRooms(
  privacyMode: SalesfloorRoomPrivacyMode,
  pageSize?: number,
): Promise<{ data: SalesfloorRoom[] }> {
  return axios.get(
    `/api/v1/salesfloor/rooms?filters={"privacyMode":"${privacyMode}"}&pageSize=${
      pageSize || 20
    }`,
  );
}

export async function getSalesfloorRoom(
  roomId: string,
): Promise<{ data: SalesfloorRoom }> {
  return axios.get(`/api/v1/salesfloor/rooms/${roomId}`);
}

export function joinSalesfloorRoom(
  roomId: string,
): Promise<{ data: SalesfloorRoomJoinResponse }> {
  return axios.post(`/api/v1/salesfloor/rooms/${roomId}/join`);
}

export function leaveSalesfloorRoom(roomId: string): Promise<{ data: SalesfloorRoom }> {
  return axios.post(`/api/v1/salesfloor/rooms/${roomId}/leave`);
}

interface SalesfloorRoomParticipantUpdatePayload {
  status?: SalesfloorRoomParticipantStatus;
}

export function updateSalesfloorRoomParticipant(
  roomId: string,
  payload: SalesfloorRoomParticipantUpdatePayload,
): Promise<{ data: SalesfloorRoom }> {
  return axios.patch(`/api/v1/salesfloor/rooms/${roomId}/participants/me`, payload);
}

export function getAiChatBot(): Promise<{ data: AIChatBot }> {
  return axios.get(`/api/v1/org/ai-chatbot`);
}

export function patchAiChatBot(
  id: number,
  updates: Partial<AIChatBot>,
): Promise<{ data: AIChatBot }> {
  return axios.patch(`/api/v1/ai-chatbots/${id}`, updates);
}

export function postTrainAiChatBot(id: number): Promise<{ data: AIChatBot }> {
  return axios.post(`/api/v1/ai-chatbots/${id}/train`);
}

export function getAiChatBotSources(
  id: number,
  page: number,
  pageSize: number,
): Promise<{
  data: Page<PolymorphicAIChatBotDataSource>;
}> {
  const filters = JSON.stringify({ sourceType: AIChatBotDataSourceType.website_link });
  return axios.get(`/api/v1/ai-chatbots/${id}/sources`, {
    params: {
      page,
      pageSize,
      filters,
    },
  });
}

export function getAiChatBotFaqs(
  id: number,
  page: number,
  pageSize: number,
): Promise<{
  data: Page<QADataSource>;
}> {
  return axios.get(`/api/v1/ai-chatbots/${id}/scope/questions`, {
    params: { page, pageSize },
  });
}

export const getPromptValidation = async (
  id: number,
): Promise<{ data: Partial<{ [k in 'error' | 'warning']: string }> }> => {
  return axios.get(`/api/v1/ai-chatbots/${id}/scope/validate`);
};

export const generateAiChatbotFaq = async (id: number) => {
  return axios.post(`/api/v1/ai-chatbots/${id}/jobs`, {
    jobType: AiChatbotJobType.GENERATE_FAQ,
  });
};

export function generateAiChatBotSingleFaq(
  id: number,
  question: string,
): Promise<{ data: QADataSource }> {
  return axios.post(`/api/v1/ai-chatbots/${id}/sources/qa/generate-answer`, {
    question,
  });
}

export function postAiChatBotSource(
  id: number,
  data: Omit<PolymorphicAIChatBotDataSource, 'id'>,
): Promise<{ data: PolymorphicAIChatBotDataSource }> {
  return axios.post(`/api/v1/ai-chatbots/${id}/sources`, {
    ...data,
  });
}

export function patchAiChatBotSource(
  chatBotId: number,
  sourceId: string | number,
  data: Partial<PatchQADataSource>,
): Promise<{ data: PatchQADataSource }> {
  return axios.patch(`/api/v1/ai-chatbots/${chatBotId}/scope/questions/${sourceId}`, {
    ...data,
  });
}

export function deleteAiChatBotSource(
  chatBotId: number,
  sourceId: number | string,
): Promise<any> {
  return axios.delete(`/api/v1/ai-chatbots/${chatBotId}/sources/${sourceId}`);
}

export function getAiChatBotSourceContents(
  chatBotId: number,
  sourceId: number | string,
): Promise<{
  data: {
    contents: string;
  };
}> {
  return axios.get(`/api/v1/ai-chatbots/${chatBotId}/sources/${sourceId}/contents`);
}

export function postAiChatBotIngestWebsite(
  id: number,
  urls: string[],
  crawl: boolean,
): Promise<{ data: {} }> {
  return axios.post(`/api/v1/ai-chatbots/${id}/jobs`, {
    jobType: AiChatbotJobType.SCRAPE,
    urls,
    crawl,
  });
}

export async function getActiveDialSessions(): Promise<{ data: DialSession[] }> {
  return axios.get(`/api/v1/outbound/dial_session?filters={"status":"OPEN"}`);
}

export function getAiPersonas(): Promise<{ data: AIPersona[] }> {
  return axios.get('/api/v1/ai_persona');
}

export function getAiPersona(id: string): Promise<{ data: AIPersona }> {
  return axios.get(`/api/v1/ai_persona/${id}`);
}

export function updateAiPersona(
  id: number,
  data: Partial<AIPersona>,
): Promise<{ data: AIPersona }> {
  return axios.put(`/api/v1/ai_persona/${id}`, data);
}

export function deleteAiPersona(id: number): Promise<void> {
  return axios.delete(`/api/v1/ai_persona/${id}`);
}

export function createAiPersona(data: AIPersonaAdd): Promise<{ data: AIPersona }> {
  return axios.post('/api/v1/ai_persona', data);
}

export function createAiPersonaVideo(data: {
  aiPersonaId: number;
  videoType: AIPersonaVideoType;
  mimetype: string;
}): Promise<{ data: S3UploadTicket }> {
  return axios.post('/api/v1/ai_persona/video/upload', {
    ai_persona_id: data.aiPersonaId,
    video_type: data.videoType?.toLocaleLowerCase(),
    mimetype: data.mimetype,
  });
}

export function completeAiPersonaVideoUpload(
  aiPersonaId: number,
): Promise<{ data: AIPersona }> {
  return axios.post('/api/v1/ai_persona/video/complete', {
    ai_persona_id: aiPersonaId,
  });
}

export function getGoals(): Promise<GoalListResponse> {
  return axios.get('/api/v1/goals');
}

export function createGoal(goal: Partial<Goal>): Promise<GoalResponse> {
  return axios.post('/api/v1/goals', goal);
}

export function getGoal(id: number): Promise<GoalResponse> {
  return axios.get(`/api/v1/goals/${id}`);
}

export function updateGoal(id: number, goal: Partial<Goal>): Promise<GoalResponse> {
  return axios.put(`/api/v1/goals/${id}`, goal);
}

export function deleteGoal(id: number): Promise<void> {
  return axios.delete(`/api/v1/goals/${id}`);
}

export function getGoalVersions(goalId: number): Promise<GoalVersionListResponse> {
  return axios.get(`/api/v1/goals/${goalId}/versions`);
}

export function createGoalVersion(
  goalId: number,
  version: Partial<GoalVersion>,
): Promise<GoalVersionResponse> {
  return axios.post(`/api/v1/goals/${goalId}/versions`, version);
}

export function getGoalVersion(
  goalId: number,
  versionId: number,
): Promise<GoalVersionResponse> {
  return axios.get(`/api/v1/goals/${goalId}/versions/${versionId}`);
}

export function deleteGoalVersion(goalId: number, versionId: number): Promise<void> {
  return axios.delete(`/api/v1/goals/${goalId}/versions/${versionId}`);
}

export function bulkDeleteAiChatBotSources(
  chatBotId: number,
  sourceIds: number[],
): Promise<void> {
  return axios.delete(`/api/v1/ai-chatbots/${chatBotId}/sources`, {
    data: { sourceIds },
  });
}

export function bulkDeleteAiChatBotQaSources(
  chatBotId: number,
  questionIds: number[],
): Promise<void> {
  return axios.delete(`/api/v1/ai-chatbots/${chatBotId}/scope/questions`, {
    data: { questionIds },
  });
}

export function getGoalConditionsSchema(): Promise<{
  data: BusinessRulesConditionsSchema;
}> {
  return axios.get('/api/v1/goals/conditions_schema');
}

export function getAiChatBotConversationStarters(
  id: number,
): Promise<{ data: ConversationStarter[] }> {
  return axios.get(`/api/v1/ai-chatbots/${id}/conversation-starters`);
}

export function createAiChatBotConversationStarter(
  id: number,
  suggestion: string,
): Promise<{ data: ConversationStarter }> {
  return axios.post(`/api/v1/ai-chatbots/${id}/conversation-starters`, {
    suggestion,
  });
}

export function updateAiChatBotConversationStarter(
  chatBotId: number,
  starterId: number,
  suggestion: string,
): Promise<{ data: ConversationStarter }> {
  return axios.patch(
    `/api/v1/ai-chatbots/${chatBotId}/conversation-starters/${starterId}`,
    { suggestion },
  );
}

export function deleteAiChatBotConversationStarter(
  chatBotId: number,
  starterId: number,
): Promise<void> {
  return axios.delete(
    `/api/v1/ai-chatbots/${chatBotId}/conversation-starters/${starterId}`,
  );
}

export function getAiChatBotRefreshConfig(
  id: number,
): Promise<{ data: AIChatBotRefreshConfig }> {
  return axios.get(`/api/v1/ai-chatbots/refresh-config/${id}`);
}

export function createAiChatBotRefreshConfig(
  data: AIChatBotRefreshConfigCreate,
): Promise<{ data: AIChatBotRefreshConfig }> {
  return axios.post('/api/v1/ai-chatbots/refresh-config', data);
}

export function updateAiChatBotRefreshConfig(
  id: number,
  data: Partial<AIChatBotRefreshConfigCreate>,
): Promise<{ data: AIChatBotRefreshConfig }> {
  return axios.put(`/api/v1/ai-chatbots/refresh-config/${id}`, data);
}

export function deleteAiChatBotRefreshConfig(id: number): Promise<void> {
  return axios.delete(`/api/v1/ai-chatbots/refresh-config/${id}`);
}

export function updateAiChatBotScopeAnswer(
  chatBotId: number,
  answerId: number,
  status: 'APPROVED' | 'REJECTED' | 'PENDING',
  answer?: string,
): Promise<{ data: FaqAnswer }> {
  return axios.patch(`/api/v1/ai-chatbots/${chatBotId}/scope/answers/${answerId}`, {
    status,
    answer,
  });
}

export function createAIChatbotQuestionWithAnswer(
  chatBotId: number,
  question: string,
  answer: string,
): Promise<{ data: QADataSource }> {
  return axios.post(`/api/v1/ai-chatbots/${chatBotId}/scope/question-with-answer`, {
    question,
    answer,
  });
}

export function duplicateGoal(id: number, name?: string): Promise<GoalResponse> {
  return axios.post(`/api/v1/goals/${id}/duplicate`, name ? { name } : {});
}

export async function downloadAiTranscript(conversation: number): Promise<Blob> {
  return axios
    .get(`/api/v1/ai-chatbots/conversation/${conversation}/transcript/download`, {
      responseType: 'blob',
    })
    .then(res => res.data);
}

export async function getConversationTranscript(
  conversation: number,
): Promise<{ data: TranscriptViewModel }> {
  return axios.get(`/api/v1/ai-chatbots/conversation/${conversation}/transcript`);
}
